<template>
  <div class="requirement-lab">
    <!-- Header -->
    <div class="lab-header">
      <div class="header-left">
        <router-link :to="`/projects/${projectId}`" class="btn-back" title="Back to project">
          <i class="fas fa-arrow-left"></i>
        </router-link>
        <div class="header-title">
          <h1><i class="fas fa-flask"></i> Check Lab</h1>
          <p>
            Architect and test
            <template v-if="checkDefinitionName">
              <strong>{{ checkDefinitionName }}</strong>
              — this project’s comprehensive check
            </template>
            <template v-else>this project’s comprehensive check</template>
          </p>
        </div>
      </div>
      <div class="header-right">
        <router-link
          v-if="canAccessResearchCampaigns"
          :to="{ path: '/campaigns', query: { fromProject: projectId } }"
          class="btn btn-ghost btn-sm campaigns-link"
          title="Research industries and draft check definitions"
        >
          <i class="fas fa-rocket" aria-hidden="true"></i>
          Research campaigns
        </router-link>
        <!-- Rule context: browse/open — not a second load path beside Test Suite -->
        <div class="rule-picker" ref="rulePickerEl">
          <div class="picker-field">
            <span class="picker-label">Working on</span>
            <button
              type="button"
              class="picker-trigger rule-trigger"
              @click="toggleRulePicker"
              :aria-expanded="rulePickerOpen ? 'true' : 'false'"
            >
              <span class="picker-trigger-main">
                <template v-if="isEditingExisting">
                  {{ requirement.name || 'Untitled rule' }}
                  <span v-if="requirement.code" class="picker-code">{{ requirement.code }}</span>
                </template>
                <template v-else>New rule draft</template>
              </span>
              <span class="picker-trigger-meta">
                {{ existingRequirements.length }} saved rules · Browse or start new
              </span>
              <i class="fas fa-chevron-down picker-caret"></i>
            </button>
          </div>
          <div v-if="rulePickerOpen" class="picker-menu">
            <div class="picker-menu-head">
              <input
                v-model="ruleSearch"
                type="search"
                class="picker-search"
                placeholder="Search rules by name, code, or policy…"
                @keydown.esc.stop="rulePickerOpen = false"
              />
              <button type="button" class="btn btn-sm btn-primary" @click="startNewRule">
                <i class="fas fa-plus"></i> New rule
              </button>
            </div>
            <div class="picker-menu-list">
              <template v-for="group in groupedFilteredRequirements" :key="group.key">
                <div class="picker-group-label">
                  <span>{{ group.label }}</span>
                  <span class="picker-group-count">{{ group.items.length }}</span>
                </div>
                <button
                  v-for="req in group.items"
                  :key="req.id"
                  type="button"
                  class="picker-option"
                  :class="{ active: String(selectedExistingId) === String(req.id) }"
                  @click="openSavedRule(req)"
                >
                  <span class="picker-option-title">{{ req.name }}</span>
                  <span class="picker-option-meta">
                    <span class="picker-code">{{ req.code }}</span>
                    <span class="picker-policy-chip">{{ policyChipLabel(req) }}</span>
                    <span v-if="req.severity" class="ts-severity-badge" :class="req.severity?.toLowerCase()">{{ req.severity }}</span>
                  </span>
                </button>
              </template>
              <p v-if="!filteredRequirements.length" class="picker-empty">No matching rules</p>
            </div>
          </div>
        </div>

        <div class="doc-picker" ref="docPickerEl" v-if="documents.length">
          <div class="picker-field">
            <span class="picker-label">Test against</span>
            <button
              type="button"
              class="picker-trigger doc-trigger"
              @click="toggleDocPicker"
              :aria-expanded="docPickerOpen ? 'true' : 'false'"
            >
              <span class="picker-trigger-main">
                {{ selectedDocument?.name || 'Select a document' }}
              </span>
              <span v-if="selectedDocument" class="picker-trigger-meta">
                <template v-if="selectedDocument.primary_entity_name">
                  {{ selectedDocument.primary_entity_name }} ·
                </template>
                {{ documentTypeLabel(selectedDocument) }}
                <template v-if="documentCoverageLabel(selectedDocument)">
                  · {{ documentCoverageLabel(selectedDocument) }}
                </template>
                <template v-if="selectedDocument.created_at">
                  · {{ formatShortDate(selectedDocument.created_at) }}
                </template>
              </span>
              <i class="fas fa-chevron-down picker-caret"></i>
            </button>
          </div>
          <div v-if="docPickerOpen" class="picker-menu doc-picker-menu">
            <div class="picker-menu-head">
              <input
                v-model="docSearch"
                type="search"
                class="picker-search"
                placeholder="Search by filename, entity, type, coverage…"
                @keydown.esc.stop="docPickerOpen = false"
              />
            </div>
            <div class="picker-menu-list">
              <button
                v-for="doc in filteredDocuments"
                :key="doc.id"
                type="button"
                class="picker-option doc-option"
                :class="{ active: selectedDocumentId === doc.id }"
                @click="selectDocument(doc.id)"
              >
                <span class="picker-option-title">{{ doc.name }}</span>
                <span class="picker-option-meta doc-option-meta">
                  <span v-if="doc.primary_entity_name" class="meta-entity">
                    <i class="fas fa-building"></i> {{ doc.primary_entity_name }}
                  </span>
                  <span v-else class="meta-muted">No entity linked</span>
                  <span class="meta-type">{{ documentTypeLabel(doc) }}</span>
                  <span v-if="documentCoverageLabel(doc)" class="meta-coverage">{{ documentCoverageLabel(doc) }}</span>
                  <span v-if="doc.created_at" class="meta-date">{{ formatShortDate(doc.created_at) }}</span>
                </span>
                <span
                  v-if="doc.party_labels?.length"
                  class="picker-option-sub"
                >{{ doc.party_labels.join(' · ') }}</span>
              </button>
              <p v-if="!filteredDocuments.length" class="picker-empty">No matching documents</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Split Pane Container -->
    <div
      class="split-pane-container"
      ref="container"
      :class="{ 'settings-mode': activeTab === 'check' }"
    >
      <!-- Left Panel: Document Viewer -->
      <div
        v-show="activeTab !== 'check'"
        class="document-panel"
        :style="{ width: leftPanelWidth + '%' }"
      >
        <div class="panel-header">
          <h3><i class="fas fa-file-pdf"></i> Document Preview</h3>
          <div class="panel-actions">
             <span v-if="selectedDocument" class="doc-badge">{{ documentTypeLabel(selectedDocument) }}</span>
             <span v-if="selectedDocument?.primary_entity_name" class="doc-badge entity-badge">
               {{ selectedDocument.primary_entity_name }}
             </span>
          </div>
        </div>
        <div class="document-content">
          <iframe 
            v-if="pdfUrl" 
            :src="pdfUrl" 
            width="100%" 
            height="100%" 
            frameborder="0"
          ></iframe>
          <div v-else-if="isNonPreviewable" class="empty-state non-previewable">
            <i class="fas fa-file-alt fa-3x"></i>
            <p><strong>{{ selectedDocument?.name }}</strong></p>
            <p class="muted">This file type cannot be previewed in browser.</p>
            <p class="muted">Testing will still work - the AI can analyze this document.</p>
          </div>
          <div v-else class="empty-state">
            <i class="fas fa-file-upload fa-3x"></i>
            <p>Select a document to begin testing</p>
          </div>
        </div>
      </div>

      <!-- Resizer -->
      <div v-show="activeTab !== 'check'" class="resizer" @mousedown="startResize"></div>

      <!-- Right Panel: Lab & Architect -->
      <div
        class="lab-panel"
        :style="{ width: activeTab === 'check' ? '100%' : (100 - leftPanelWidth) + '%' }"
      >
        <div class="panel-header">
          <div class="tabs">
            <button
              type="button"
              @click="activeTab = 'check'"
              :class="{ active: activeTab === 'check' }"
              title="Name, column cell policy, and rules on this check"
            >
              <i class="fas fa-clipboard-check"></i> This check
            </button>
            <button 
              @click="activeTab = 'architect'" 
              :class="{ active: activeTab === 'architect' }"
            >
              <i class="fas fa-magic"></i> Architect Chat
            </button>
            <button 
              @click="activeTab = 'editor'" 
              :class="{ active: activeTab === 'editor' }"
            >
              <i class="fas fa-edit"></i> Prompt Editor
            </button>
            <button 
              @click="loadVersionHistory(); activeTab = 'versions'" 
              :class="{ active: activeTab === 'versions' }"
              :disabled="!isEditingExisting"
              :title="!isEditingExisting ? 'Open a saved rule to see prompt history' : ''"
            >
              <i class="fas fa-history"></i> Versions
            </button>
            <button 
              @click="activeTab = 'testsuite'" 
              :class="{ active: activeTab === 'testsuite' }"
            >
              <i class="fas fa-flask"></i> Test Suite
            </button>
          </div>
        </div>

        <div class="panel-content">
          <div v-if="activeTab === 'check'" class="tab-pane check-settings-pane">
            <CheckLabDefinitionPanel
              v-if="checkDefinitionId"
              :definition-id="checkDefinitionId"
              @saved="onDefinitionSaved"
            />
            <div v-else class="empty-state">
              <i class="fas fa-clipboard-check fa-3x"></i>
              <p><strong>No check assigned to this project</strong></p>
              <p class="muted">Assign a comprehensive check in project settings before you can edit policy or rules here.</p>
              <router-link :to="`/projects/${projectId}/settings`" class="btn btn-primary btn-sm">
                Open project settings
              </router-link>
            </div>
          </div>
          <!-- Architect Chat Tab -->
          <div v-if="activeTab === 'architect'" class="tab-pane architect-pane">
            <div class="chat-messages" ref="chatContainer">
              <div v-if="messages.length === 0" class="welcome-message">
                <div class="ai-avatar">
                  <i class="fas fa-robot"></i>
                </div>
                <h3>Welcome to Check Lab</h3>
                <p>Describe what you want to check in the document. I’ll help you architect the requirement and write the analysis prompt — then you can load it into the editor, test the draft, and save it to the check.</p>
                <div class="suggestions">
                  <button type="button" class="suggestion-chip" @click="sendSuggestion('Check if General Liability coverage is at least $2M')">
                    Check for $2M General Liability
                  </button>
                  <button type="button" class="suggestion-chip" @click="sendSuggestion('Verify if there is a Waiver of Subrogation endorsement')">
                    Verify Waiver of Subrogation
                  </button>
                </div>
              </div>

              <div v-for="msg in messages" :key="msg.timestamp" class="message" :class="msg.role">
                <div class="message-content">
                  <div class="text" v-html="formatMessage(msg.content)"></div>
                  
                  <!-- Proposed Requirement UI -->
                  <div v-if="msg.requirement" class="proposed-req">
                    <div class="req-header">
                      <strong>{{ msg.requirement.name }}</strong>
                      <span class="badge" :class="msg.requirement.severity?.toLowerCase()">{{ msg.requirement.severity }}</span>
                    </div>
                    <div class="req-desc">{{ msg.requirement.description }}</div>
                    <div class="req-prompt">
                      <pre><code>{{ msg.requirement.prompt_template }}</code></pre>
                    </div>
                    <div class="req-actions">
                      <button @click="useRequirement(msg.requirement)" class="btn btn-sm btn-success">
                        <i class="fas fa-check"></i> Load into Editor
                      </button>
                    </div>
                  </div>

                  <div v-if="msg.actions && msg.actions.length" class="actions">
                    <button 
                      v-for="action in msg.actions" 
                      :key="action.label"
                      type="button"
                      @click="executeAction(action)"
                      class="btn btn-sm btn-outline"
                    >
                      <i :class="action.icon"></i> {{ architectActionLabel(action) }}
                    </button>
                  </div>
                </div>
              </div>

              <div v-if="isTyping" class="message assistant typing">
                <div class="typing-indicator"><span></span><span></span><span></span></div>
              </div>
            </div>

            <!-- Image Previews above chat input -->
            <div v-if="chatImages.length > 0" class="chat-image-previews">
              <div 
                v-for="(image, index) in chatImages" 
                :key="index" 
                class="chat-image-preview"
              >
                <img 
                  v-if="image.type !== 'application/pdf'" 
                  :src="image.preview" 
                  :alt="image.name"
                >
                <div v-else class="pdf-icon">
                  <i class="fas fa-file-pdf"></i>
                </div>
                <button @click="removeChatImage(index)" class="remove-image" title="Remove">
                  <i class="fas fa-times"></i>
                </button>
              </div>
            </div>

            <div v-if="imageUploadError" class="image-error">
              <i class="fas fa-exclamation-circle"></i> {{ imageUploadError }}
            </div>

            <div 
              class="chat-input"
              :class="{ 'dragging': isDraggingImage }"
              @dragover.prevent="isDraggingImage = true"
              @dragleave.prevent="isDraggingImage = false"
              @drop.prevent="handleImageDrop"
            >
              <input 
                type="file" 
                ref="imageInput"
                @change="handleImageSelect"
                accept="image/png,image/jpeg,image/jpg,application/pdf"
                multiple
                style="display: none;"
              >
              <button 
                @click="triggerImageInput" 
                class="attach-btn"
                title="Attach screenshot (PNG, JPG, PDF)"
                :disabled="chatImages.length >= 5"
              >
                <i class="fas fa-image"></i>
              </button>
              <textarea 
                v-model="inputMessage" 
                @keydown.enter.prevent="sendMessage"
                :placeholder="chatImages.length > 0 ? 'Describe what the screenshot shows...' : 'Describe your compliance check...'"
                rows="2"
              ></textarea>
              <button @click="sendMessage" :disabled="!inputMessage.trim() || isTyping" class="send-btn">
                <i class="fas fa-paper-plane"></i>
              </button>
            </div>
          </div>

          <!-- Editor Tab -->
          <div v-if="activeTab === 'editor'" class="tab-pane editor-pane">
            <div class="editor-scroll">
              <!-- Rule Context Banner -->
              <div class="rule-context-banner" :class="isEditingExisting ? 'banner-blue' : 'banner-green'">
                <template v-if="isEditingExisting">
                  <i class="fas fa-edit"></i> Editing:
                  <strong>{{ requirement.name || 'Untitled rule' }}</strong>
                  <span v-if="requirement.code" class="code-chip">{{ requirement.code }}</span>
                  <span
                    v-if="requirement.prompt_type === 'checkbox_verification'"
                    class="code-chip checkbox-chip"
                  >
                    Checkbox · {{ requirement.form_type || 'ACORD' }} · expect {{ requirement.expected_value }}
                  </span>
                  <button type="button" @click="deleteRequirement" class="btn btn-sm btn-danger banner-delete-btn">
                    <i class="fas fa-trash"></i> Delete
                  </button>
                </template>
                <template v-else>
                  <i class="fas fa-plus-circle"></i> Creating new rule (draft — not on the check yet)
                </template>
              </div>

              <div class="form-row identity-row">
                <div class="form-group grow">
                  <label>Requirement Name</label>
                  <input v-model="requirement.name" class="form-control" placeholder="e.g. Workers Comp Limit Check" />
                </div>
                <div class="form-group half">
                  <label>Severity</label>
                  <select v-model="requirement.severity" class="form-control">
                    <option value="CRITICAL">Critical</option>
                    <option value="HIGH">High</option>
                    <option value="MEDIUM">Medium</option>
                    <option value="LOW">Low</option>
                  </select>
                </div>
                <div class="form-group half">
                  <label>Code</label>
                  <input v-model="requirement.code" class="form-control" readonly />
                </div>
              </div>

              <!-- Checkbox accuracy — elevated when relevant -->
              <div
                v-if="requirement.prompt_type === 'checkbox_verification'"
                class="checkbox-accuracy-panel"
              >
                <div class="section-divider">
                  <i class="fas fa-check-square"></i> Checkbox accuracy
                </div>
                <p class="panel-help">
                  These fields drive specialized ACORD checkbox prompts and batching.
                  Wrong expected value produces false accuracy results.
                </p>
                <div class="form-row">
                  <div class="form-group grow">
                    <label>Field / checkbox description</label>
                    <input
                      v-model="requirement.field_description"
                      class="form-control"
                      placeholder="e.g. Workers Compensation Per Statute checkbox on ACORD 25"
                    >
                  </div>
                  <div class="form-group half">
                    <label>Expected value</label>
                    <select v-model="requirement.expected_value" class="form-control">
                      <option value="checked">Checked / X mark present</option>
                      <option value="unchecked">Unchecked / Empty</option>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>
                  </div>
                  <div class="form-group half">
                    <label>Form type</label>
                    <select v-model="requirement.form_type" class="form-control">
                      <option value="ACORD">ACORD Form</option>
                      <option value="STANDARD">Standard Form</option>
                      <option value="CUSTOM">Custom Form</option>
                    </select>
                  </div>
                </div>
              </div>

              <div class="form-group prompt-hero">
                <label>Prompt template</label>
                <textarea
                  v-model="requirement.prompt_template"
                  class="form-control prompt-editor"
                  rows="14"
                  placeholder="Write the analysis instructions for the AI..."
                ></textarea>
                <span class="prompt-char-counter" :class="{ 'counter-warn': (requirement.prompt_template?.length || 0) > 3000 }">
                  {{ requirement.prompt_template?.length || 0 }} characters{{ (requirement.prompt_template?.length || 0) > 3000 ? ' — consider shortening' : '' }}
                </span>
              </div>

              <details class="editor-details" :open="!isEditingExisting || detailsOpen">
                <summary @click.prevent="detailsOpen = !detailsOpen">
                  Details &amp; filters
                  <i :class="detailsOpen ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
                </summary>
                <div class="editor-details-body">
                  <div class="form-group">
                    <label>Description <span class="label-hint">(internal notes)</span></label>
                    <input v-model="requirement.description" class="form-control" placeholder="Brief description of what this checks..." />
                  </div>

                  <div class="form-group">
                    <label>Prompt type</label>
                    <select v-model="requirement.prompt_type" class="form-control">
                      <option v-for="pt in PROMPT_TYPES" :key="pt.value" :value="pt.value">{{ pt.label }}</option>
                    </select>
                    <span class="label-hint" v-if="requirement.prompt_type !== 'default'">
                      Activates specialized analysis template for this check type.
                    </span>
                  </div>

                  <div class="form-group">
                    <label>
                      Applies to — document types
                      <span class="label-hint">leave blank to run on all</span>
                    </label>
                    <div class="filter-checkbox-grid">
                      <label v-for="dt in DOCUMENT_TYPES" :key="dt.value" class="filter-chip">
                        <input
                          type="checkbox"
                          :value="dt.value"
                          v-model="requirement.applicable_document_types"
                        >
                        {{ dt.label }}
                      </label>
                    </div>
                  </div>

                  <div class="form-group">
                    <label>
                      Applies to — coverage types
                      <span class="label-hint">leave blank to run on all</span>
                    </label>
                    <div class="filter-checkbox-grid">
                      <label v-for="ct in COVERAGE_TYPES" :key="ct.value" class="filter-chip">
                        <input
                          type="checkbox"
                          :value="ct.value"
                          v-model="requirement.applicable_coverage_types"
                        >
                        {{ ct.label }}
                      </label>
                    </div>
                  </div>

                  <div class="form-group checkbox-inline">
                    <label class="checkbox-label">
                      <input
                        type="checkbox"
                        v-model="requirement.requires_temporal_context"
                      >
                      <span><strong>Include current date context</strong> — for expiration / validity checks</span>
                    </label>
                  </div>
                </div>
              </details>

              <div v-if="saveStatus" class="save-status-banner" :class="`save-status-${saveStatus.type}`">
                <i :class="saveStatus.type === 'success' ? 'fas fa-check-circle' : 'fas fa-exclamation-circle'"></i>
                {{ saveStatus.message }}
              </div>

              <div class="lab-parity-note">
                <i class="fas fa-info-circle"></i>
                <span>
                  <strong>Test this draft</strong> is diagnostic only — it does not update the check.
                  <strong>Save to check</strong> writes the CheckRequirement (and a version snapshot).
                  Production may still skip rules by coverage type; agent emails use saved wording, not Lab output.
                </span>
              </div>

              <!-- Test Results -->
              <div v-if="testResult" class="test-results-container">
                <div class="result-header" :class="testResult.result?.toLowerCase()">
                  <div class="result-title">
                    <i :class="getResultIcon(testResult.result)"></i>
                    <span>Result: {{ testResult.result }}</span>
                  </div>
                  <div class="result-meta">
                    <span v-if="lastTestModel && lastTestModel.model" class="model-badge" :title="'Provider: ' + lastTestModel.provider">
                      <i class="fas fa-microchip"></i> {{ lastTestModel.model }}
                    </span>
                    <span v-if="lastResponseTime" class="response-time" :class="responseTimeClass">
                      <i class="fas fa-clock"></i> {{ lastResponseTime }}s
                    </span>
                    <span class="confidence">Confidence: {{ (testResult.confidence * 100).toFixed(0) }}%</span>
                  </div>
                </div>

                <div class="spatial-strip" :class="`spatial-${spatialGrounding?.status || 'unknown'}`">
                  <i class="fas fa-crosshairs"></i>
                  <span>
                    <strong>Spatial grounding (LiteParse):</strong>
                    {{ spatialGroundingLabel }}
                  </span>
                </div>

                <p class="diagnostic-only-note">
                  Diagnostic only — does not update the check until you <strong>Save to check</strong>.
                </p>

                <div class="result-body">
                  <div class="result-section">
                    <label>Evidence</label>
                    <div class="evidence-box">{{ testResult.evidence }}</div>
                  </div>
                  <div class="result-section">
                    <label>Reasoning</label>
                    <p>{{ testResult.reasoning }}</p>
                  </div>
                  <div v-if="testResult.deficiencies?.length" class="result-section">
                    <label>Deficiencies</label>
                    <ul>
                      <li v-for="d in testResult.deficiencies" :key="d">{{ d }}</li>
                    </ul>
                  </div>
                </div>

                <div class="result-footer">
                  <p>Not accurate? Tell the Architect what went wrong.</p>
                  <button type="button" @click="feedbackToArchitect" class="btn btn-sm btn-outline">
                    <i class="fas fa-comment-dots"></i> Give Feedback
                  </button>
                </div>
              </div>
            </div>

            <!-- Sticky footer: Test draft vs Save to check -->
            <div class="editor-sticky-footer">
              <span class="draft-badge" :class="draftStatusClass">{{ draftStatusLabel }}</span>
              <div class="editor-sticky-actions">
                <button
                  type="button"
                  @click="runTest"
                  class="btn btn-outline"
                  :disabled="testing || !selectedDocumentId || !requirement.prompt_template"
                >
                  <i :class="testing ? 'fas fa-spinner fa-spin' : 'fas fa-vial'"></i>
                  {{ testing ? 'Testing draft…' : 'Test this draft' }}
                </button>
                <button
                  type="button"
                  @click="saveToDefinition"
                  class="btn btn-primary"
                  :disabled="!requirement.prompt_template || saving"
                >
                  <i :class="saving ? 'fas fa-spinner fa-spin' : 'fas fa-save'"></i>
                  {{ saving ? 'Saving…' : 'Save to check' }}
                </button>
              </div>
            </div>
          </div>

          <!-- Versions Tab -->
          <div v-if="activeTab === 'versions'" class="tab-pane versions-pane">
            <div class="versions-header">
              <h4><i class="fas fa-history"></i> Version History</h4>
              <p class="subtitle">Compare and manage prompt versions for {{ requirement.name }}</p>
            </div>
            
            <div v-if="loadingVersions" class="loading-state">
              <i class="fas fa-spinner fa-spin"></i> Loading versions...
            </div>
            
            <div v-else-if="!(versionHistory.versions?.length)" class="empty-versions">
              <div v-if="livePromptPreview" class="version-card active-version">
                <div class="version-header">
                  <span class="version-badge active">Live</span>
                  <span class="version-label">Current prompt on the check</span>
                </div>
                <div class="prompt-preview">
                  <pre>{{ livePromptPreview }}</pre>
                </div>
              </div>
              <i class="fas fa-code-branch fa-2x"></i>
              <p>No saved snapshots yet</p>
              <p class="muted">
                Analysis runs and project activity do not create versions — only
                <strong>Save to check</strong> or Snapshot below. Live prompt is shown above when available.
              </p>
              <button
                type="button"
                class="btn btn-primary"
                :disabled="!requirement.prompt_template || snapshotting"
                @click="snapshotCurrentPrompt"
              >
                <i :class="snapshotting ? 'fas fa-spinner fa-spin' : 'fas fa-camera'"></i>
                {{ snapshotting ? 'Snapshotting…' : 'Snapshot current prompt' }}
              </button>
            </div>
            
            <div v-else class="versions-content">
              <!-- Current Active Prompt -->
              <div class="version-card active-version">
                <div class="version-header">
                  <span class="version-badge active">Active</span>
                  <span class="version-label">Current Prompt</span>
                </div>
                <div class="prompt-preview">
                  <pre>{{ versionHistory.active_prompt }}</pre>
                </div>
              </div>
              
              <!-- Version Comparison -->
              <div v-if="compareVersionId" class="comparison-view">
                <div class="comparison-header">
                  <h5>Comparing with Version {{ selectedCompareVersion?.version }}</h5>
                  <button @click="compareVersionId = null" class="btn btn-sm btn-ghost">
                    <i class="fas fa-times"></i> Close
                  </button>
                </div>
                <div class="comparison-panels">
                  <div class="compare-panel">
                    <label>Current (Active)</label>
                    <pre>{{ versionHistory.active_prompt }}</pre>
                  </div>
                  <div class="compare-panel">
                    <label>Version {{ selectedCompareVersion?.version }}</label>
                    <pre>{{ selectedCompareVersion?.enhanced_prompt || selectedCompareVersion?.original_prompt }}</pre>
                  </div>
                </div>
                <div class="comparison-actions">
                  <button @click="revertToVersion(selectedCompareVersion)" class="btn btn-warning">
                    <i class="fas fa-undo"></i> Revert to This Version
                  </button>
                </div>
              </div>
              
              <!-- Version List -->
              <div class="version-list">
                <h5>Previous Versions</h5>
                <div 
                  v-for="version in versionHistory.versions" 
                  :key="version.id" 
                  class="version-item"
                  :class="{ 'is-active': version.is_active }"
                >
                  <div class="version-info">
                    <span class="version-number">v{{ version.version }}</span>
                    <span class="version-date">{{ formatDate(version.created_at) }}</span>
                    <span v-if="version.is_active" class="version-badge active">Active</span>
                    <span v-if="version.feedback_driven" class="version-badge feedback">From Feedback</span>
                  </div>
                  <div class="version-meta">
                    <span v-if="version.created_by_username">by {{ version.created_by_username }}</span>
                    <span v-if="version.notes" class="version-notes">{{ version.notes }}</span>
                  </div>
                  <div class="version-actions">
                    <button @click="compareVersionId = version.id" class="btn btn-sm btn-ghost">
                      <i class="fas fa-exchange-alt"></i> Compare
                    </button>
                    <button 
                      v-if="!version.is_active" 
                      @click="revertToVersion(version)" 
                      class="btn btn-sm btn-outline"
                    >
                      <i class="fas fa-undo"></i> Revert
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Test Suite Tab -->
          <div v-if="activeTab === 'testsuite'" class="tab-pane testsuite-pane">
            <div class="testsuite-header">
              <div class="testsuite-header-top">
                <div class="testsuite-title">
                  <h4><i class="fas fa-flask"></i> Test Suite</h4>
                  <p class="subtitle">Expand any rule to read what it checks, then run one rule or the full suite (saved prompts only — Editor draft is not included)</p>
                </div>
                <button
                  type="button"
                  @click="runTestSuite"
                  class="btn btn-primary"
                  :disabled="testSuiteRunningAll || selectedRulesCount === 0 || !selectedDocumentId"
                >
                  <i :class="testSuiteRunningAll ? 'fas fa-spinner fa-spin' : 'fas fa-play'"></i>
                  {{ testSuiteRunningAll ? 'Running...' : 'Run suite on saved rules' }}
                </button>
              </div>

              <div v-if="isDirty" class="testsuite-warning">
                <i class="fas fa-exclamation-triangle"></i>
                You have an unsaved Editor draft — the suite still uses <strong>saved</strong> prompts until you Save to check.
              </div>

              <div v-if="!selectedDocumentId" class="testsuite-warning">
                <i class="fas fa-exclamation-triangle"></i> Select a document in the header to run tests.
              </div>

              <!-- Document Type Context Banner -->
              <div v-if="selectedDocument" class="doc-context-banner">
                <i class="fas fa-file-alt"></i>
                Testing against: <strong>{{ selectedDocument.name }}</strong>
                <span v-if="selectedDocument.primary_entity_name" class="doc-type-chip entity-chip">
                  {{ selectedDocument.primary_entity_name }}
                </span>
                <span v-if="documentTypeLabel(selectedDocument)" class="doc-type-chip">
                  {{ documentTypeLabel(selectedDocument) }}
                </span>
                <span v-if="documentCoverageLabel(selectedDocument)" class="doc-type-chip">
                  {{ documentCoverageLabel(selectedDocument) }}
                </span>
                <span v-if="existingRequirements.some(r => r.applicable_document_types?.length)" class="filter-note">
                  — some rules are filtered by document type and may be skipped
                </span>
              </div>

              <div class="testsuite-controls">
                <button @click="toggleSelectAll" class="btn btn-sm btn-ghost">
                  <i :class="allSelected ? 'fas fa-check-square' : 'far fa-square'"></i>
                  {{ allSelected ? 'Deselect All' : 'Select All' }}
                </button>
                <span class="selection-counter">{{ selectedRulesCount }} of {{ existingRequirements.length }} selected</span>
              </div>
            </div>

            <div v-if="testSuiteHasResults" class="testsuite-results-digest">
              <div class="digest-header">
                <div>
                  <h5><i class="fas fa-list-check"></i> Results review</h5>
                  <p class="digest-subtitle">All rules from this run — grouped by outcome. You do not need to expand each row to review failures.</p>
                </div>
                <div class="digest-actions">
                  <button type="button" class="btn btn-sm btn-ghost" @click="expandFailuresOnly">
                    Expand failures in list
                  </button>
                  <button type="button" class="btn btn-sm btn-ghost" @click="expandAllWithResults">
                    Expand all in list
                  </button>
                  <button type="button" class="btn btn-sm btn-ghost" @click="collapseAllExpanded">
                    Collapse list
                  </button>
                </div>
              </div>

              <section v-if="testSuiteDigestGroups.fail.length" class="digest-group digest-fail">
                <h6><i class="fas fa-times-circle"></i> Failed ({{ testSuiteDigestGroups.fail.length }})</h6>
                <article
                  v-for="item in testSuiteDigestGroups.fail"
                  :key="'fail-' + item.req.id"
                  class="digest-card"
                >
                  <div class="digest-card-head">
                    <strong>{{ item.req.name }}</strong>
                    <span class="ts-code-chip">{{ item.req.code }}</span>
                    <button type="button" class="btn btn-sm btn-ghost" @click="scrollToAndExpandRule(item.req.id)">
                      Jump to rule
                    </button>
                  </div>
                  <p v-if="item.result.reasoning" class="digest-excerpt">{{ item.result.reasoning }}</p>
                  <p v-if="item.result.evidence" class="digest-evidence">{{ truncateText(item.result.evidence, 280) }}</p>
                </article>
              </section>

              <section v-if="testSuiteDigestGroups.partial.length" class="digest-group digest-partial">
                <h6><i class="fas fa-exclamation-circle"></i> Partial ({{ testSuiteDigestGroups.partial.length }})</h6>
                <article
                  v-for="item in testSuiteDigestGroups.partial"
                  :key="'partial-' + item.req.id"
                  class="digest-card"
                >
                  <div class="digest-card-head">
                    <strong>{{ item.req.name }}</strong>
                    <span class="ts-code-chip">{{ item.req.code }}</span>
                    <button type="button" class="btn btn-sm btn-ghost" @click="scrollToAndExpandRule(item.req.id)">
                      Jump to rule
                    </button>
                  </div>
                  <p v-if="item.result.reasoning" class="digest-excerpt">{{ item.result.reasoning }}</p>
                </article>
              </section>

              <section v-if="testSuiteDigestGroups.pass.length" class="digest-group digest-pass">
                <h6><i class="fas fa-check-circle"></i> Passed ({{ testSuiteDigestGroups.pass.length }})</h6>
                <div class="digest-pass-list">
                  <button
                    v-for="item in testSuiteDigestGroups.pass"
                    :key="'pass-' + item.req.id"
                    type="button"
                    class="digest-pass-chip"
                    @click="scrollToAndExpandRule(item.req.id)"
                  >
                    {{ item.req.name }}
                    <span v-if="testSuiteTiming[item.req.id]" class="digest-timing">{{ testSuiteTiming[item.req.id] }}s</span>
                  </button>
                </div>
              </section>

              <section v-if="testSuiteDigestGroups.error.length" class="digest-group digest-error">
                <h6><i class="fas fa-bug"></i> Errors ({{ testSuiteDigestGroups.error.length }})</h6>
                <article
                  v-for="item in testSuiteDigestGroups.error"
                  :key="'error-' + item.req.id"
                  class="digest-card"
                >
                  <div class="digest-card-head">
                    <strong>{{ item.req.name }}</strong>
                    <button type="button" class="btn btn-sm btn-ghost" @click="scrollToAndExpandRule(item.req.id)">
                      Jump to rule
                    </button>
                  </div>
                  <p class="digest-excerpt">{{ item.result.reasoning || item.result.evidence }}</p>
                </article>
              </section>
            </div>

            <div v-if="existingRequirements.length === 0" class="empty-state">
              <i class="fas fa-list-check fa-3x"></i>
              <p>No rules found in this check definition.</p>
            </div>

            <div v-else class="testsuite-list">
              <section
                v-for="group in groupedExistingRequirements"
                :key="group.key"
                class="testsuite-group"
              >
                <div class="testsuite-group-head">
                  <label class="ts-checkbox ts-group-select">
                    <input
                      type="checkbox"
                      :checked="isPolicyGroupSelected(group)"
                      :indeterminate.prop="isPolicyGroupPartial(group)"
                      @change="togglePolicyGroupSelection(group)"
                    />
                  </label>
                  <h5>{{ group.label }}</h5>
                  <span class="testsuite-group-count">{{ group.items.length }}</span>
                </div>
              <div
                v-for="req in group.items"
                :key="req.id"
                :id="'testsuite-row-' + req.id"
                class="testsuite-row"
                :class="{
                  'row-expanded': expandedTestResults[req.id],
                  'row-pass': testSuiteResults[req.id]?.result?.toUpperCase() === 'PASS',
                  'row-fail': testSuiteResults[req.id]?.result?.toUpperCase() === 'FAIL',
                  'row-partial': testSuiteResults[req.id]?.result?.toUpperCase() === 'PARTIAL',
                  'row-error': testSuiteResults[req.id]?.result?.toUpperCase() === 'ERROR'
                }"
              >
                <div class="testsuite-row-main">
                  <label class="ts-checkbox" @click.stop>
                    <input type="checkbox" v-model="testSuiteSelections[req.id]" />
                  </label>

                  <button
                    type="button"
                    class="ts-expand-toggle"
                    :aria-expanded="!!expandedTestResults[req.id]"
                    :title="expandedTestResults[req.id] ? 'Hide check details' : 'Show check details'"
                    @click="toggleTestResultExpand(req.id)"
                  >
                    <i :class="expandedTestResults[req.id] ? 'fas fa-chevron-down' : 'fas fa-chevron-right'"></i>
                  </button>

                  <button
                    type="button"
                    class="ts-rule-info ts-rule-info-clickable"
                    @click="toggleTestResultExpand(req.id)"
                  >
                    <div class="ts-rule-headline">
                      <span class="ts-rule-name">{{ req.name }}</span>
                      <span class="ts-code-chip">{{ req.code }}</span>
                      <span class="ts-policy-chip">{{ policyChipLabel(req) }}</span>
                      <span class="ts-severity-badge" :class="req.severity?.toLowerCase()">{{ req.severity }}</span>
                      <span v-if="getReqPromptType(req) !== 'default'" class="ts-type-chip">
                        {{ getReqPromptTypeLabel(req) }}
                      </span>
                      <span v-if="req.applicable_document_types?.length" class="ts-filter-chip" :title="`Only runs on: ${req.applicable_document_types.join(', ')}`">
                        <i class="fas fa-filter"></i> {{ req.applicable_document_types.join(', ') }}
                      </span>
                    </div>
                    <p v-if="!expandedTestResults[req.id]" class="ts-rule-preview">
                      <template v-if="testSuiteResults[req.id]?.reasoning">
                        <span class="ts-result-snippet" :class="testSuiteResults[req.id].result?.toLowerCase()">
                          {{ truncateText(testSuiteResults[req.id].reasoning, 160) }}
                        </span>
                      </template>
                      <template v-else>
                        {{ getReqSummaryLine(req) }}
                      </template>
                    </p>
                  </button>

                  <div class="ts-result-area">
                    <i v-if="testSuiteRunning[req.id]" class="fas fa-spinner fa-spin ts-spinner"></i>
                    <template v-else-if="testSuiteResults[req.id]">
                      <span class="ts-result-badge" :class="testSuiteResults[req.id].result?.toLowerCase()">
                        <i :class="getResultIcon(testSuiteResults[req.id].result)"></i>
                        {{ testSuiteResults[req.id].result }}
                        <span class="ts-confidence">{{ (testSuiteResults[req.id].confidence * 100).toFixed(0) }}%</span>
                      </span>
                      <span v-if="testSuiteTiming[req.id]" class="ts-timing" :class="getTimingClass(testSuiteTiming[req.id])">
                        <i class="fas fa-clock"></i> {{ testSuiteTiming[req.id] }}s
                      </span>
                    </template>
                  </div>

                  <button
                    type="button"
                    class="btn btn-sm btn-primary ts-run-btn"
                    title="Run this rule against the selected document"
                    :disabled="!selectedDocumentId || testSuiteRunning[req.id] || testSuiteRunningAll"
                    @click.stop="runSingleTest(req)"
                  >
                    <i :class="testSuiteRunning[req.id] ? 'fas fa-spinner fa-spin' : 'fas fa-play'"></i>
                    Run
                  </button>

                  <button type="button" @click.stop="editRuleFromTestSuite(req)" class="btn btn-sm btn-outline ts-edit-btn" title="Open this rule in the Prompt Editor">
                    <i class="fas fa-arrow-right"></i> Open in Editor
                  </button>
                </div>

                <!-- Expandable check definition + optional test results -->
                <div v-if="expandedTestResults[req.id]" class="ts-details">
                  <div class="ts-check-details">
                    <h6><i class="fas fa-clipboard-check"></i> What this rule checks</h6>
                    <p v-if="getReqDescription(req)" class="ts-check-lead">{{ getReqDescription(req) }}</p>
                    <p v-else class="ts-check-lead ts-muted">No short description — see the saved prompt below.</p>

                    <dl class="ts-meta-grid">
                      <div v-if="getReqPromptType(req) !== 'default'">
                        <dt>Check type</dt>
                        <dd>{{ getReqPromptTypeLabel(req) }}</dd>
                      </div>
                      <div v-if="getReqFieldDescription(req)">
                        <dt>Field / checkbox</dt>
                        <dd>{{ getReqFieldDescription(req) }}</dd>
                      </div>
                      <div v-if="getReqExpectedValue(req)">
                        <dt>Expected</dt>
                        <dd>{{ formatExpectedValue(getReqExpectedValue(req)) }}</dd>
                      </div>
                      <div v-if="req.applicable_document_types?.length">
                        <dt>Document types</dt>
                        <dd>{{ req.applicable_document_types.join(', ') }}</dd>
                      </div>
                      <div v-if="req.applicable_coverage_types?.length">
                        <dt>Coverage types</dt>
                        <dd>{{ req.applicable_coverage_types.join(', ') }}</dd>
                      </div>
                      <div>
                        <dt>Temporal context</dt>
                        <dd>{{ req.requires_temporal_context === false ? 'Off' : 'On (uses current date)' }}</dd>
                      </div>
                    </dl>

                    <div v-if="getReqPromptTemplate(req)" class="ts-detail-section">
                      <label>Saved prompt</label>
                      <pre class="ts-prompt-preview">{{ getReqPromptTemplate(req) }}</pre>
                    </div>
                  </div>

                  <div v-if="testSuiteResults[req.id]" class="ts-result-details">
                    <h6><i class="fas fa-microscope"></i> Last test result</h6>
                    <div class="ts-detail-section">
                      <label>Evidence</label>
                      <div class="evidence-box">{{ testSuiteResults[req.id].evidence }}</div>
                    </div>
                    <div class="ts-detail-section">
                      <label>Reasoning</label>
                      <p>{{ testSuiteResults[req.id].reasoning }}</p>
                    </div>
                    <div v-if="testSuiteResults[req.id].deficiencies?.length" class="ts-detail-section">
                      <label>Deficiencies</label>
                      <ul>
                        <li v-for="d in testSuiteResults[req.id].deficiencies" :key="d">{{ d }}</li>
                      </ul>
                    </div>
                  </div>
                  <div v-else-if="selectedDocumentId" class="ts-no-result-hint">
                    <i class="fas fa-info-circle"></i>
                    Click <strong>Run</strong> to test this rule against {{ selectedDocument.name }}.
                  </div>
                  <div v-else class="ts-no-result-hint">
                    <i class="fas fa-info-circle"></i>
                    Select a document in the header, then click <strong>Run</strong>.
                  </div>
                </div>
              </div>
              </section>
            </div>

            <!-- Summary Row -->
            <div v-if="testSuiteHasResults" class="testsuite-summary">
              <span class="summary-label">Summary:</span>
              <span class="summary-badge pass"><i class="fas fa-check-circle"></i> {{ testSuiteSummary.pass }} Pass</span>
              <span class="summary-badge fail"><i class="fas fa-times-circle"></i> {{ testSuiteSummary.fail }} Fail</span>
              <span class="summary-badge partial"><i class="fas fa-exclamation-circle"></i> {{ testSuiteSummary.partial }} Partial</span>
              <span v-if="testSuiteSummary.error" class="summary-badge error"><i class="fas fa-bug"></i> {{ testSuiteSummary.error }} Error</span>
              <span v-if="testSuiteTotalTime > 0" class="summary-badge timing" :class="getTimingClass(testSuiteTotalTime)">
                <i class="fas fa-clock"></i> {{ testSuiteTotalTime.toFixed(1) }}s total
              </span>
              <span v-if="estimatedProductionTime > 0" class="summary-badge production-estimate" :class="estimatedProductionTime > 60 ? 'warn' : 'ok'">
                <i class="fas fa-server"></i> ~{{ estimatedProductionTime.toFixed(0) }}s in production (parallel)
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount, computed, nextTick, watch } from 'vue';
import { useRoute } from 'vue-router';
import api from '@/services/api';
import auth from '@/services/auth';
import { sanitizeLlmResponse } from '@/utils/sanitize';
import { getDocumentUrl as getDocumentUrlHelper } from '@/utils/documentUrlHelper';
import {
  groupRequirementsByPolicy,
  policyChipLabel,
  policyMetaForRequirement,
} from '@/utils/requirementPolicyGroups';
import CheckLabDefinitionPanel from '@/components/comprehensive/CheckLabDefinitionPanel.vue';

export default {
  name: 'RequirementLab',
  components: { CheckLabDefinitionPanel },
  
  setup() {
    const route = useRoute();
    // const router = useRouter(); // Available if needed for navigation
    const projectId = ref(route.params.projectId);

    const canAccessResearchCampaigns = computed(() => (
      auth.hasAnyRole([
        'PLATFORM_ADMIN',
        'PLATFORM_ENGINEER',
        'ORG_ADMIN',
        'HEALTHCARE_ADMIN',
        'COMPLIANCE_ADMIN',
        'ADMIN',
        'COMPLIANCE_OPS',
      ])
    ));
    
    // Layout State
    const leftPanelWidth = ref(50);
    const activeTab = ref('architect');
    const detailsOpen = ref(false);
    const saving = ref(false);
    const snapshotting = ref(false);
    const savedSnapshot = ref('');
    const draftTestedUnsaved = ref(false);
    const spatialGrounding = ref(null);
    
    // Data State
    const documents = ref([]);
    const selectedDocumentId = ref(null);
    const existingRequirements = ref([]);
    const selectedExistingId = ref(null);
    const isEditingExisting = ref(false);
    const checkDefinitionId = ref(null);
    const checkDefinitionName = ref('');
    const columnCellPolicy = ref('verify_in_force');
    const saveStatus = ref(null); // { type: 'success'|'error', message: string }

    // Header pickers
    const rulePickerOpen = ref(false);
    const docPickerOpen = ref(false);
    const ruleSearch = ref('');
    const docSearch = ref('');
    const rulePickerEl = ref(null);
    const docPickerEl = ref(null);
    const requirement = ref({
      id: null,
      name: '',
      code: '',
      description: '',
      severity: 'MEDIUM',
      prompt_template: '',
      requires_temporal_context: true,
      prompt_type: 'default',
      applicable_document_types: [],
      applicable_coverage_types: [],
      field_description: '',
      expected_value: 'checked',
      form_type: 'ACORD'
    });

    const DOCUMENT_TYPES = [
      { value: 'CERTIFICATE', label: 'Certificate of Insurance' },
      { value: 'INSURANCE_POLICY', label: 'Insurance Policy' },
      { value: 'ENDORSEMENT', label: 'Endorsement' },
      { value: 'LOSS_RUN', label: 'Loss Run' },
      { value: 'CONTRACT', label: 'Contract' },
      { value: 'AGREEMENT', label: 'Agreement' },
      { value: 'AMENDMENT', label: 'Amendment' },
      { value: 'W9_FORM', label: 'W-9 Form' },
      { value: 'OTHER', label: 'Other' },
    ];

    const COVERAGE_TYPES = [
      { value: 'GENERAL_LIABILITY', label: 'General Liability' },
      { value: 'WORKERS_COMP', label: 'Workers Comp' },
      { value: 'AUTO_LIABILITY', label: 'Auto Liability' },
      { value: 'UMBRELLA', label: 'Umbrella/Excess' },
      { value: 'PROFESSIONAL_LIABILITY', label: 'E&O / Professional' },
      { value: 'PROPERTY', label: 'Property' },
      { value: 'CYBER', label: 'Cyber' },
      { value: 'BUILDERS_RISK', label: "Builder's Risk" },
    ];

    const PROMPT_TYPES = [
      { value: 'default', label: 'Default — standard compliance check' },
      { value: 'checkbox_verification', label: 'Checkbox Verification — ACORD form marks/checkboxes' },
      { value: 'integrity_check', label: 'Integrity Check — detect tampered certificates' },
      { value: 'coverage_amount', label: 'Coverage Amount — verify limits and deductibles' },
      { value: 'date_check', label: 'Date Check — expiration and effective dates' },
      { value: 'entity_verification', label: 'Entity Verification — named insureds, holders, AI' },
    ];
    
    // Chat State
    const messages = ref([]);
    const inputMessage = ref('');
    const isTyping = ref(false);
    const chatContainer = ref(null);
    
    // Image Upload State for Chat
    const chatImages = ref([]);
    const isDraggingImage = ref(false);
    const imageUploadError = ref(null);
    const imageInput = ref(null);
    const maxImages = 5;
    const maxFileSize = 5 * 1024 * 1024; // 5MB
    const allowedImageTypes = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'];
    
    // Testing State
    const testing = ref(false);
    const testResult = ref(null);
    const lastResponseTime = ref(null);
    const lastPromptStats = ref(null);
    const lastTestModel = ref(null);
    
    // Version History State
    const loadingVersions = ref(false);
    const versionHistory = ref({ versions: [], active_prompt: '', original_prompt: '' });
    const compareVersionId = ref(null);

    // Test Suite State
    const testSuiteSelections = ref({});
    const testSuiteResults = ref({});
    const testSuiteRunning = ref({});
    const testSuiteRunningAll = ref(false);
    const expandedTestResults = ref({});
    const testSuiteTiming = ref({});

    // Computed
    const selectedDocument = computed(() => {
      return documents.value.find(d => d.id === selectedDocumentId.value);
    });

    const filteredRequirements = computed(() => {
      const q = (ruleSearch.value || '').trim().toLowerCase();
      if (!q) return existingRequirements.value;
      return existingRequirements.value.filter((req) => {
        const policy = policyMetaForRequirement(req);
        const hay = [
          req.name || '',
          req.code || '',
          req.severity || '',
          policy.label,
          policy.short,
          (req.applicable_coverage_types || []).join(' '),
        ].join(' ').toLowerCase();
        return hay.includes(q);
      });
    });

    const groupedFilteredRequirements = computed(() =>
      groupRequirementsByPolicy(filteredRequirements.value)
    );

    const groupedExistingRequirements = computed(() =>
      groupRequirementsByPolicy(existingRequirements.value)
    );

    const filteredDocuments = computed(() => {
      const q = (docSearch.value || '').trim().toLowerCase();
      if (!q) return documents.value;
      return documents.value.filter((doc) => {
        const type = documentTypeLabel(doc).toLowerCase();
        const cov = (documentCoverageLabel(doc) || '').toLowerCase();
        const parties = (doc.party_labels || []).join(' ').toLowerCase();
        const hay = [
          doc.name || '',
          doc.primary_entity_name || '',
          type,
          cov,
          parties,
        ].join(' ').toLowerCase();
        return hay.includes(q);
      });
    });
    
    // Check if file can be previewed in iframe (PDFs and images only)
    const canPreviewInIframe = (filename) => {
      if (!filename) return false;
      const ext = filename.toLowerCase().split('.').pop();
      const previewableFormats = ['pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'];
      return previewableFormats.includes(ext);
    };
    
    const pdfUrl = computed(() => {
      if (!selectedDocument.value) return null;

      // Wait for presigned URL before attempting to load — raw S3 URLs return 403
      if (!selectedDocument.value.file_url) return null;
      
      const filename = selectedDocument.value.name || selectedDocument.value.file_url || selectedDocument.value.file || '';
      if (!canPreviewInIframe(filename)) {
        return null;
      }
      
      const url = getDocumentUrlHelper(selectedDocument.value);
      return url !== '#' ? url : null;
    });
    
    const isNonPreviewable = computed(() => {
      if (!selectedDocument.value) return false;
      if (!selectedDocument.value.file_url) return false;
      const url = getDocumentUrlHelper(selectedDocument.value);
      const filename = selectedDocument.value.name || selectedDocument.value.file_url || selectedDocument.value.file || '';
      return url !== '#' && !canPreviewInIframe(filename);
    });
    
    const selectedCompareVersion = computed(() => {
      if (!compareVersionId.value || !versionHistory.value.versions) return null;
      return versionHistory.value.versions.find(v => v.id === compareVersionId.value);
    });

    const selectedRulesCount = computed(() =>
      Object.values(testSuiteSelections.value).filter(Boolean).length
    );

    const allSelected = computed(() =>
      existingRequirements.value.length > 0 &&
      selectedRulesCount.value === existingRequirements.value.length
    );

    const testSuiteHasResults = computed(() =>
      Object.keys(testSuiteResults.value).length > 0
    );

    const testSuiteSummary = computed(() => {
      const results = Object.values(testSuiteResults.value);
      return {
        pass: results.filter(r => r?.result?.toUpperCase() === 'PASS').length,
        fail: results.filter(r => r?.result?.toUpperCase() === 'FAIL').length,
        partial: results.filter(r => r?.result?.toUpperCase() === 'PARTIAL').length,
        error: results.filter(r => r?.result?.toUpperCase() === 'ERROR').length,
      };
    });

    const testSuiteResultItems = computed(() =>
      existingRequirements.value
        .filter((req) => testSuiteResults.value[req.id])
        .map((req) => ({
          req,
          result: testSuiteResults.value[req.id],
          timing: testSuiteTiming.value[req.id],
        }))
    );

    const testSuiteDigestGroups = computed(() => {
      const groups = { fail: [], partial: [], pass: [], error: [] };
      testSuiteResultItems.value.forEach((item) => {
        const key = (item.result?.result || 'ERROR').toUpperCase();
        if (key === 'FAIL') groups.fail.push(item);
        else if (key === 'PARTIAL') groups.partial.push(item);
        else if (key === 'PASS') groups.pass.push(item);
        else groups.error.push(item);
      });
      return groups;
    });

    const responseTimeClass = computed(() => {
      if (!lastResponseTime.value) return '';
      if (lastResponseTime.value <= 3) return 'fast';
      if (lastResponseTime.value <= 6) return 'moderate';
      return 'slow';
    });

    const testSuiteTotalTime = computed(() => {
      return Object.values(testSuiteTiming.value).reduce((sum, t) => sum + (t || 0), 0);
    });

    const estimatedProductionTime = computed(() => {
      const times = Object.values(testSuiteTiming.value).filter(t => t > 0);
      if (times.length === 0) return 0;
      const avgTime = times.reduce((s, t) => s + t, 0) / times.length;
      const totalChecks = selectedRulesCount.value || times.length;
      const parallelWorkers = 8;
      const batches = Math.ceil(totalChecks / parallelWorkers);
      return batches * avgTime;
    });

    const requirementFingerprint = computed(() => JSON.stringify({
      name: requirement.value.name,
      code: requirement.value.code,
      description: requirement.value.description,
      severity: requirement.value.severity,
      prompt_template: requirement.value.prompt_template,
      requires_temporal_context: requirement.value.requires_temporal_context,
      prompt_type: requirement.value.prompt_type,
      applicable_document_types: requirement.value.applicable_document_types,
      applicable_coverage_types: requirement.value.applicable_coverage_types,
      field_description: requirement.value.field_description,
      expected_value: requirement.value.expected_value,
      form_type: requirement.value.form_type,
    }));

    const isDirty = computed(() => {
      if (!savedSnapshot.value) {
        return Boolean(requirement.value.prompt_template || requirement.value.name);
      }
      return requirementFingerprint.value !== savedSnapshot.value;
    });

    const draftStatusLabel = computed(() => {
      if (!requirement.value.prompt_template) return 'Empty draft';
      if (!isDirty.value && isEditingExisting.value) return 'Saved to check';
      if (draftTestedUnsaved.value && isDirty.value) return 'Draft tested — not saved';
      if (isDirty.value) return 'Unsaved draft';
      return isEditingExisting.value ? 'Saved to check' : 'New draft';
    });

    const draftStatusClass = computed(() => {
      if (!isDirty.value && isEditingExisting.value) return 'draft-saved';
      if (draftTestedUnsaved.value && isDirty.value) return 'draft-tested';
      if (isDirty.value) return 'draft-dirty';
      return 'draft-neutral';
    });

    const livePromptPreview = computed(() => {
      return versionHistory.value.active_prompt
        || requirement.value.prompt_template
        || '';
    });

    const spatialGroundingLabel = computed(() => {
      const s = spatialGrounding.value;
      if (!s) return 'Not reported for this test';
      if (s.status === 'engaged') {
        const cache = s.cached ? ' (cached)' : '';
        const policy = s.column_cell_policy === 'strict_certificate'
          ? ' Strict certificate (blank boxes fail).'
          : s.column_cell_policy === 'verify_in_force'
            ? ' Verify in force (other evidence may fill a blank).'
            : '';
        return `Engaged — ${s.pairings_chars} chars of pairings${cache}.${policy} Same path as production.`;
      }
      if (s.status === 'unavailable') {
        return 'Unavailable — LiteParse sidecar down; fail-open (prompt-only), same as production.';
      }
      if (s.status === 'skipped') {
        return `Skipped — document type ${s.document_type || 'n/a'} is not eligible for ACORD pairings.`;
      }
      if (s.status === 'empty') {
        return 'No pairings detected (sidecar up). Preamble may still apply for checkbox wording.';
      }
      return s.status;
    });

    const markSavedSnapshot = () => {
      savedSnapshot.value = requirementFingerprint.value;
      draftTestedUnsaved.value = false;
    };

    const getTimingClass = (seconds) => {
      if (seconds <= 3) return 'fast';
      if (seconds <= 6) return 'moderate';
      return 'slow';
    };

    // Methods
    const formatShortDate = (value) => {
      if (!value) return '';
      try {
        return new Date(value).toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
        });
      } catch {
        return String(value);
      }
    };

    const documentTypeLabel = (doc) => {
      if (!doc) return '';
      return doc.classified_document_type || doc.document_type || 'Unknown type';
    };

    const documentCoverageLabel = (doc) => {
      if (!doc) return '';
      const types = Array.isArray(doc.coverage_types) ? doc.coverage_types.filter(Boolean) : [];
      if (types.length) return types.slice(0, 4).join(', ');
      const meta = doc.coverage_metadata;
      if (meta && typeof meta === 'object') {
        const keys = Object.keys(meta).filter(Boolean);
        if (keys.length) return keys.slice(0, 4).join(', ');
      }
      return '';
    };

    const confirmDiscardIfDirty = () => {
      if (!isDirty.value) return true;
      return window.confirm('You have unsaved draft changes. Discard them and continue?');
    };

    const closePickers = () => {
      rulePickerOpen.value = false;
      docPickerOpen.value = false;
    };

    const toggleRulePicker = () => {
      docPickerOpen.value = false;
      rulePickerOpen.value = !rulePickerOpen.value;
      if (rulePickerOpen.value) {
        ruleSearch.value = '';
      }
    };

    const toggleDocPicker = () => {
      rulePickerOpen.value = false;
      docPickerOpen.value = !docPickerOpen.value;
      if (docPickerOpen.value) {
        docSearch.value = '';
      }
    };

    const onDocumentClick = (event) => {
      const t = event.target;
      if (rulePickerOpen.value && rulePickerEl.value && !rulePickerEl.value.contains(t)) {
        rulePickerOpen.value = false;
      }
      if (docPickerOpen.value && docPickerEl.value && !docPickerEl.value.contains(t)) {
        docPickerOpen.value = false;
      }
    };

    const selectDocument = (docId) => {
      selectedDocumentId.value = docId;
      docPickerOpen.value = false;
    };

    const startNewRule = () => {
      if (!confirmDiscardIfDirty()) return;
      selectedExistingId.value = null;
      loadExistingRequirement();
      activeTab.value = 'editor';
      closePickers();
    };

    const openSavedRule = (req) => {
      if (!confirmDiscardIfDirty()) return;
      selectedExistingId.value = req.id;
      loadExistingRequirement();
      activeTab.value = 'editor';
      closePickers();
    };

    const loadDocuments = async () => {
      try {
        const response = await api.get(`/projects/${projectId.value}/documents/`, {
          params: { skip_file_urls: 'true', include_lab_context: 'true' }
        });
        documents.value = response.data.documents || [];
        if (documents.value.length > 0) {
          selectedDocumentId.value = documents.value[0].id;
        }
      } catch (error) {
        console.error('Error loading documents:', error);
      }
    };

    const fetchDocumentUrl = async (docId) => {
      try {
        const response = await api.get(`/projects/${projectId.value}/documents/${docId}/`);
        const doc = response.data.document;
        if (doc && doc.file_url) {
          const idx = documents.value.findIndex(d => d.id === docId);
          if (idx !== -1) {
            documents.value[idx] = { ...documents.value[idx], file_url: doc.file_url };
          }
        }
      } catch (error) {
        console.error('Error fetching document URL:', error);
      }
    };
    
    const loadExistingRequirements = async () => {
      try {
        // First get the project's default check definition
        const projectResponse = await api.get(`/projects/${projectId.value}/`);
        const project = projectResponse.data.project || projectResponse.data;
        
        // default_comprehensive_check is just the ID (number), not an object
        if (project.default_comprehensive_check) {
          checkDefinitionId.value = project.default_comprehensive_check;
          
          // Load the check definition which includes requirements
          const defResponse = await api.get(`/comprehensive-check-definitions/${checkDefinitionId.value}/`);
          existingRequirements.value = defResponse.data.requirements || [];
          checkDefinitionName.value = defResponse.data.name || '';
          columnCellPolicy.value = defResponse.data.column_cell_policy || 'verify_in_force';
        }
      } catch (error) {
        console.error('Error loading existing requirements:', error);
      }
    };
    
    const loadExistingRequirement = () => {
      if (!selectedExistingId.value) {
        resetRequirement();
        isEditingExisting.value = false;
        testResult.value = null;
        spatialGrounding.value = null;
        savedSnapshot.value = '';
        draftTestedUnsaved.value = false;
        return;
      }
      
      const existing = existingRequirements.value.find(
        r => String(r.id) === String(selectedExistingId.value)
      );
      if (existing) {
        const vl = existing.validation_logic || {};
        requirement.value = {
          id: existing.id,
          name: existing.name,
          code: existing.code,
          description: existing.description || '',
          severity: existing.severity || 'MEDIUM',
          prompt_template: existing.prompt_template || vl.prompt_template || '',
          requires_temporal_context: existing.requires_temporal_context !== false,
          prompt_type: vl.prompt_type || 'default',
          applicable_document_types: existing.applicable_document_types || [],
          applicable_coverage_types: existing.applicable_coverage_types || [],
          field_description: vl.field_description || '',
          expected_value: vl.expected_value || 'checked',
          form_type: vl.form_type || 'ACORD',
        };
        isEditingExisting.value = true;
        testResult.value = null;
        spatialGrounding.value = null;
        activeTab.value = 'editor';
        detailsOpen.value = false;
        nextTick(() => markSavedSnapshot());
      }
    };
    
    const sendMessage = async () => {
      const text = inputMessage.value.trim();
      if (!text || isTyping.value) return;
      
      // Prepare images for submission
      const images = chatImages.value.map(img => ({
        name: img.name,
        type: img.type,
        base64: img.base64
      }));
      
      // Show user message with image indicators
      const userMessage = {
        role: 'user',
        content: text,
        timestamp: new Date().toISOString(),
        imageCount: images.length
      };
      messages.value.push(userMessage);
      
      inputMessage.value = '';
      clearChatImages(); // Clear images after sending
      scrollToBottom();
      
      isTyping.value = true;
      try {
        const response = await api.post('/compliance-health/agent/architect/chat/', {
          message: text,
          project_id: projectId.value,
          context: {
            history: messages.value.map(m => ({ role: m.role, content: m.content })),
            current_requirement: requirement.value
          },
          // Include images if any were uploaded
          images: images.length > 0 ? images : null
        });
        
        messages.value.push({
          role: 'assistant',
          content: response.data.message,
          requirement: response.data.requirement,
          actions: response.data.actions,
          timestamp: new Date().toISOString()
        });
      } catch (error) {
        console.error('Chat error:', error);
        messages.value.push({
          role: 'assistant',
          content: 'Sorry, I hit a snag. Please try again.',
          timestamp: new Date().toISOString()
        });
      } finally {
        isTyping.value = false;
        scrollToBottom();
      }
    };
    
    // Image upload methods
    const triggerImageInput = () => {
      imageInput.value?.click();
    };
    
    const handleImageSelect = (event) => {
      const files = event.target.files;
      if (files) {
        processImages(Array.from(files));
      }
      event.target.value = '';
    };
    
    const handleImageDrop = (event) => {
      isDraggingImage.value = false;
      const files = event.dataTransfer?.files;
      if (files) {
        processImages(Array.from(files));
      }
    };
    
    const processImages = async (files) => {
      imageUploadError.value = null;
      
      for (const file of files) {
        if (chatImages.value.length >= maxImages) {
          imageUploadError.value = `Maximum ${maxImages} images allowed`;
          break;
        }
        
        if (!allowedImageTypes.includes(file.type)) {
          imageUploadError.value = `Invalid file type: ${file.name}. Only PNG, JPG, and PDF allowed.`;
          continue;
        }
        
        if (file.size > maxFileSize) {
          imageUploadError.value = `File too large: ${file.name}. Max size is 5MB.`;
          continue;
        }
        
        try {
          const base64 = await fileToBase64(file);
          const preview = file.type === 'application/pdf' ? null : URL.createObjectURL(file);
          
          chatImages.value.push({
            name: file.name,
            type: file.type,
            size: file.size,
            base64: base64,
            preview: preview
          });
        } catch (error) {
          console.error('Error processing image:', error);
          imageUploadError.value = `Error processing ${file.name}`;
        }
      }
    };
    
    const fileToBase64 = (file) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
          const base64 = reader.result.split(',')[1];
          resolve(base64);
        };
        reader.onerror = error => reject(error);
      });
    };
    
    const removeChatImage = (index) => {
      if (chatImages.value[index].preview) {
        URL.revokeObjectURL(chatImages.value[index].preview);
      }
      chatImages.value.splice(index, 1);
      imageUploadError.value = null;
    };
    
    const clearChatImages = () => {
      chatImages.value.forEach(img => {
        if (img.preview) URL.revokeObjectURL(img.preview);
      });
      chatImages.value = [];
      imageUploadError.value = null;
    };
    
    const formatFileSize = (bytes) => {
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    };
    
    const sendSuggestion = (text) => {
      inputMessage.value = text;
      sendMessage();
    };
    
    const useRequirement = (req) => {
      requirement.value = {
        id: null,
        name: req.name || '',
        code: req.code || `REQ_${Math.random().toString(36).substr(2, 9).toUpperCase()}`,
        description: req.description || '',
        severity: req.severity || 'MEDIUM',
        prompt_template: req.prompt_template || '',
        requires_temporal_context: req.requires_temporal_context !== false,
        prompt_type: req.prompt_type || req.validation_logic?.prompt_type || 'default',
        applicable_document_types: req.applicable_document_types || [],
        applicable_coverage_types: req.applicable_coverage_types || [],
        field_description: req.field_description || req.validation_logic?.field_description || '',
        expected_value: req.expected_value || req.validation_logic?.expected_value || 'checked',
        form_type: req.form_type || req.validation_logic?.form_type || 'ACORD',
      };
      // Architect proposals without an id are drafts — never overwrite an existing rule id
      if (req.id) {
        requirement.value.id = req.id;
        isEditingExisting.value = true;
        selectedExistingId.value = req.id;
        markSavedSnapshot();
      } else {
        isEditingExisting.value = false;
        selectedExistingId.value = null;
        savedSnapshot.value = '';
        draftTestedUnsaved.value = false;
      }
      testResult.value = null;
      spatialGrounding.value = null;
      activeTab.value = 'editor';
      detailsOpen.value = !req.id;
    };
    
    const runTest = async () => {
      if (!selectedDocumentId.value || !requirement.value.prompt_template) return;

      testing.value = true;
      testResult.value = null;
      lastResponseTime.value = null;
      lastPromptStats.value = null;
      spatialGrounding.value = null;

      try {
        const response = await api.post('/compliance-health/agent/test-prompt/', {
          document_id: selectedDocumentId.value,
          prompt_template: requirement.value.prompt_template,
          requirement_name: requirement.value.name,
          severity: requirement.value.severity,
          requires_temporal_context: requirement.value.requires_temporal_context !== false,
          column_cell_policy: columnCellPolicy.value,
        });

        if (response.data.success) {
          testResult.value = response.data.result;
          lastResponseTime.value = response.data.response_time || null;
          lastPromptStats.value = response.data.prompt_stats || null;
          lastTestModel.value = {
            provider: response.data.provider || '',
            model: response.data.model || ''
          };
          spatialGrounding.value = response.data.spatial_grounding || null;
          if (isDirty.value) {
            draftTestedUnsaved.value = true;
          }
        }
      } catch (error) {
        console.error('Test error:', error);
        alert('Failed to run test. See console for details.');
      } finally {
        testing.value = false;
      }
    };

    const architectActionLabel = (action) => {
      if (!action) return '';
      if (action.type === 'apply_to_editor' || action.type === 'load_requirement') {
        return 'Load into Editor';
      }
      const label = action.label || '';
      if (/apply|use this requirement/i.test(label) && !/save/i.test(label)) {
        return 'Load into Editor';
      }
      return label;
    };
    
    const feedbackToArchitect = () => {
      activeTab.value = 'architect';
      inputMessage.value = `The test result was ${testResult.value.result}. Evidence: ${testResult.value.evidence}. Reasoning: ${testResult.value.reasoning}. It missed...`;
      nextTick(() => {
        const textarea = document.querySelector('.chat-input textarea');
        textarea?.focus();
      });
    };
    
    const executeAction = (action) => {
      // Handle different action types from the architect chat
      if (!action) return;
      
      switch (action.type) {
        case 'load_requirement':
        case 'apply_to_editor':
          // Both action types load requirement into the editor
          if (action.requirement) {
            useRequirement(action.requirement);
          }
          break;
        case 'run_test':
        case 'test':
          if (selectedDocumentId.value && requirement.value.prompt_template) {
            runTest();
          }
          break;
        case 'save':
        case 'save_to_definition':
          saveToDefinition();
          break;
        case 'refine':
          activeTab.value = 'architect';
          if (action.message) {
            inputMessage.value = action.message;
          }
          break;
        case 'switch_tab':
          if (action.tab) {
            activeTab.value = action.tab;
          }
          break;
        default:
          // If action has a message, send it to the chat
          if (action.message) {
            inputMessage.value = action.message;
            sendMessage();
          }
          console.log('Unknown action type:', action.type, action);
      }
    };
    
    const showSaveStatus = (type, message) => {
      saveStatus.value = { type, message };
      setTimeout(() => { saveStatus.value = null; }, 6000);
    };

    const buildValidationLogic = () => {
      const vl = { prompt_type: requirement.value.prompt_type };
      if (requirement.value.prompt_type === 'checkbox_verification') {
        vl.field_description = requirement.value.field_description;
        vl.expected_value = requirement.value.expected_value;
        vl.form_type = requirement.value.form_type;
      }
      return vl;
    };

    const saveVersionSnapshot = async (notes = 'Saved from Check Lab') => {
      if (!requirement.value.id) return null;
      const response = await api.post(`/check-requirements/${requirement.value.id}/save_version/`, {
        prompt_template: requirement.value.prompt_template,
        notes,
        apply_immediately: true
      });
      return response.data;
    };

    const saveToDefinition = async () => {
      if (!checkDefinitionId.value) {
        showSaveStatus('error', 'No check definition found for this project');
        return;
      }

      saving.value = true;
      try {
        const validationLogic = buildValidationLogic();
        const basePayload = {
          name: requirement.value.name,
          description: requirement.value.description,
          severity: requirement.value.severity,
          prompt_template: requirement.value.prompt_template,
          requires_temporal_context: requirement.value.requires_temporal_context,
          applicable_document_types: requirement.value.applicable_document_types,
          applicable_coverage_types: requirement.value.applicable_coverage_types,
          validation_logic: validationLogic,
        };

        if (isEditingExisting.value && requirement.value.id) {
          await api.patch(`/check-requirements/${requirement.value.id}/`, basePayload);
        } else {
          const createResponse = await api.post(`/check-requirements/`, {
            ...basePayload,
            check_definition: checkDefinitionId.value,
            code: requirement.value.code || `REQ_${Math.random().toString(36).substr(2, 9).toUpperCase()}`,
            is_active: true,
          });
          requirement.value.id = createResponse.data.id;
          requirement.value.code = createResponse.data.code || requirement.value.code;
          isEditingExisting.value = true;
          selectedExistingId.value = createResponse.data.id;
        }

        await loadExistingRequirements();

        let versionNote = 'Saved to check';
        try {
          const versionData = await saveVersionSnapshot('Saved from Check Lab');
          const ver = versionData?.version || versionData?.enhancement_log?.version;
          versionNote = ver ? `Saved to check · version ${ver}` : 'Saved to check · version snapshot created';
        } catch (versionError) {
          console.error('Could not save version:', versionError);
          showSaveStatus(
            'error',
            'Check requirement saved, but version snapshot failed: '
              + (versionError.response?.data?.error || versionError.message)
          );
          markSavedSnapshot();
          return;
        }

        markSavedSnapshot();
        showSaveStatus('success', versionNote);
      } catch (error) {
        console.error('Error saving requirement:', error);
        showSaveStatus('error', 'Failed to save: ' + (error.response?.data?.error || error.message));
      } finally {
        saving.value = false;
      }
    };

    const snapshotCurrentPrompt = async () => {
      if (!requirement.value.id || !requirement.value.prompt_template) {
        showSaveStatus('error', 'Load a saved requirement before snapshotting');
        return;
      }
      snapshotting.value = true;
      try {
        const versionData = await saveVersionSnapshot('Manual snapshot from Check Lab');
        await loadVersionHistory();
        const ver = versionData?.version || versionData?.enhancement_log?.version;
        showSaveStatus('success', ver ? `Snapshot created · version ${ver}` : 'Snapshot created');
        markSavedSnapshot();
      } catch (error) {
        showSaveStatus('error', 'Snapshot failed: ' + (error.response?.data?.error || error.message));
      } finally {
        snapshotting.value = false;
      }
    };
    
    const toggleSelectAll = () => {
      const newVal = !allSelected.value;
      const newSelections = {};
      existingRequirements.value.forEach(req => {
        newSelections[req.id] = newVal;
      });
      testSuiteSelections.value = newSelections;
    };

    const isPolicyGroupSelected = (group) =>
      group.items.length > 0 && group.items.every((req) => testSuiteSelections.value[req.id]);

    const isPolicyGroupPartial = (group) => {
      const selected = group.items.filter((req) => testSuiteSelections.value[req.id]).length;
      return selected > 0 && selected < group.items.length;
    };

    const togglePolicyGroupSelection = (group) => {
      const nextVal = !isPolicyGroupSelected(group);
      const newSelections = { ...testSuiteSelections.value };
      group.items.forEach((req) => {
        newSelections[req.id] = nextVal;
      });
      testSuiteSelections.value = newSelections;
    };

    const runOneRequirementTest = async (req) => {
      testSuiteRunning.value = { ...testSuiteRunning.value, [req.id]: true };
      try {
        const response = await api.post('/compliance-health/agent/test-prompt/', {
          document_id: selectedDocumentId.value,
          prompt_template: req.prompt_template || req.validation_logic?.prompt_template,
          requirement_name: req.name,
          severity: req.severity,
          requires_temporal_context: req.requires_temporal_context !== false,
          column_cell_policy: columnCellPolicy.value,
        });
        if (response.data.success) {
          testSuiteResults.value = { ...testSuiteResults.value, [req.id]: response.data.result };
          testSuiteTiming.value = { ...testSuiteTiming.value, [req.id]: response.data.response_time || 0 };
        }
      } catch (error) {
        console.error(`Test error for ${req.name}:`, error);
        testSuiteResults.value = {
          ...testSuiteResults.value,
          [req.id]: { result: 'ERROR', confidence: 0, evidence: 'Test failed to run.', reasoning: error.message }
        };
      } finally {
        testSuiteRunning.value = { ...testSuiteRunning.value, [req.id]: false };
      }
    };

    const runSingleTest = async (req) => {
      if (!selectedDocumentId.value) {
        alert('Please select a document to test against.');
        return;
      }
      expandedTestResults.value = { ...expandedTestResults.value, [req.id]: true };
      await runOneRequirementTest(req);
    };

    const runTestSuite = async () => {
      if (!selectedDocumentId.value) {
        alert('Please select a document to test against.');
        return;
      }

      const selectedReqs = existingRequirements.value.filter(
        req => testSuiteSelections.value[req.id]
      );

      if (selectedReqs.length === 0) {
        alert('Please select at least one rule to test.');
        return;
      }

      testSuiteRunningAll.value = true;
      testSuiteTiming.value = {};

      // Run tests in parallel batches of 4 to avoid overwhelming the API
      const batchSize = 4;
      for (let i = 0; i < selectedReqs.length; i += batchSize) {
        const batch = selectedReqs.slice(i, i + batchSize);
        await Promise.all(batch.map(runOneRequirementTest));
      }

      testSuiteRunningAll.value = false;
      autoExpandAfterBatchRun(selectedReqs.map((req) => req.id));
    };

    const truncateText = (text, max = 160) => {
      const value = (text || '').trim();
      if (!value) return '';
      return value.length > max ? `${value.slice(0, max)}…` : value;
    };

    const autoExpandAfterBatchRun = (reqIds) => {
      const next = { ...expandedTestResults.value };
      const ranCount = reqIds.filter((id) => testSuiteResults.value[id]).length;
      reqIds.forEach((id) => {
        const result = testSuiteResults.value[id];
        if (!result) return;
        const status = (result.result || '').toUpperCase();
        if (status === 'FAIL' || status === 'PARTIAL' || status === 'ERROR') {
          next[id] = true;
        } else if (ranCount <= 8) {
          // Small batch (typical 4–5 rule spot-check): show every result expanded.
          next[id] = true;
        }
      });
      expandedTestResults.value = next;
    };

    const expandFailuresOnly = () => {
      const next = { ...expandedTestResults.value };
      testSuiteResultItems.value.forEach(({ req, result }) => {
        const status = (result?.result || '').toUpperCase();
        if (status === 'FAIL' || status === 'PARTIAL' || status === 'ERROR') {
          next[req.id] = true;
        }
      });
      expandedTestResults.value = next;
    };

    const expandAllWithResults = () => {
      const next = { ...expandedTestResults.value };
      testSuiteResultItems.value.forEach(({ req }) => {
        next[req.id] = true;
      });
      expandedTestResults.value = next;
    };

    const collapseAllExpanded = () => {
      expandedTestResults.value = {};
    };

    const scrollToAndExpandRule = async (reqId) => {
      expandedTestResults.value = { ...expandedTestResults.value, [reqId]: true };
      await nextTick();
      const el = document.getElementById(`testsuite-row-${reqId}`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    };

    const getReqPromptTemplate = (req) =>
      (req.prompt_template || req.validation_logic?.prompt_template || '').trim();

    const getReqPromptType = (req) =>
      req.prompt_type || req.validation_logic?.prompt_type || 'default';

    const getReqPromptTypeLabel = (req) => {
      const pt = getReqPromptType(req);
      const match = PROMPT_TYPES.find(p => p.value === pt);
      return match?.label || pt.replace(/_/g, ' ');
    };

    const getReqFieldDescription = (req) =>
      (req.field_description || req.validation_logic?.field_description || '').trim();

    const getReqExpectedValue = (req) =>
      req.expected_value || req.validation_logic?.expected_value || '';

    const formatExpectedValue = (value) => {
      const labels = {
        checked: 'Checked / X mark present',
        unchecked: 'Unchecked / empty',
        yes: 'Yes',
        no: 'No',
      };
      return labels[value] || value;
    };

    const getReqDescription = (req) => {
      const desc = (req.description || '').trim();
      if (desc) return desc;
      const field = getReqFieldDescription(req);
      if (field) return field;
      const prompt = getReqPromptTemplate(req);
      if (!prompt) return '';
      const firstLine = prompt.split('\n').map(l => l.trim()).find(Boolean);
      return firstLine || prompt.slice(0, 200);
    };

    const getReqSummaryLine = (req) => {
      const text = getReqDescription(req) || 'Expand to view the full saved prompt.';
      return text.length > 140 ? `${text.slice(0, 140)}…` : text;
    };

    const editRuleFromTestSuite = (req) => {
      if (!confirmDiscardIfDirty()) return;
      useRequirement(req);
      activeTab.value = 'editor';
      closePickers();
    };

    const toggleTestResultExpand = (reqId) => {
      expandedTestResults.value = {
        ...expandedTestResults.value,
        [reqId]: !expandedTestResults.value[reqId]
      };
    };

    const resetRequirement = () => {
      requirement.value = {
        id: null, name: '', code: '', description: '', severity: 'MEDIUM',
        prompt_template: '', requires_temporal_context: true,
        prompt_type: 'default', applicable_document_types: [], applicable_coverage_types: [],
        field_description: '', expected_value: 'checked', form_type: 'ACORD',
      };
    };

    const deleteRequirement = async () => {
      if (!isEditingExisting.value || !requirement.value.id) return;

      if (!confirm(`Delete "${requirement.value.name}"? This cannot be undone.`)) return;

      try {
        await api.delete(`/check-requirements/${requirement.value.id}/`);
        resetRequirement();
        isEditingExisting.value = false;
        selectedExistingId.value = null;
        testResult.value = null;
        await loadExistingRequirements();
        showSaveStatus('success', 'Rule deleted.');
      } catch (error) {
        console.error('Error deleting requirement:', error);
        showSaveStatus('error', 'Failed to delete: ' + (error.response?.data?.error || error.message));
      }
    };

    // Version History Methods
    const loadVersionHistory = async () => {
      if (!requirement.value.id) return;
      
      loadingVersions.value = true;
      try {
        const response = await api.get(`/check-requirements/${requirement.value.id}/versions/`);
        versionHistory.value = response.data;
      } catch (error) {
        console.error('Error loading version history:', error);
      } finally {
        loadingVersions.value = false;
      }
    };
    
    const revertToVersion = async (version) => {
      if (!confirm(`Revert to version ${version.version}? This will replace the current prompt.`)) {
        return;
      }
      
      try {
        await api.post(`/check-requirements/${requirement.value.id}/revert_version/`, {
          version_id: version.id
        });
        
        // Reload the requirement to get updated prompt
        await loadExistingRequirements();
        loadExistingRequirement();
        
        // Reload version history
        await loadVersionHistory();
        
        alert(`Reverted to version ${version.version}`);
        compareVersionId.value = null;
      } catch (error) {
        console.error('Error reverting version:', error);
        alert('Failed to revert: ' + (error.response?.data?.error || error.message));
      }
    };
    
    const formatDate = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };
    
    const formatMessage = (content) => sanitizeLlmResponse(content);
    
    const scrollToBottom = () => {
      nextTick(() => {
        if (chatContainer.value) {
          chatContainer.value.scrollTop = chatContainer.value.scrollHeight;
        }
      });
    };
    
    const getResultIcon = (result) => {
      switch(result?.toUpperCase()) {
        case 'PASS': return 'fas fa-check-circle';
        case 'FAIL': return 'fas fa-times-circle';
        case 'PARTIAL': return 'fas fa-exclamation-circle';
        default: return 'fas fa-question-circle';
      }
    };
    
    // Resize logic
    const startResize = () => {
      const container = chatContainer.value?.closest('.split-pane-container');
      if (!container) return;
      
      const onMouseMove = (moveEvent) => {
        const containerRect = container.getBoundingClientRect();
        const newWidth = ((moveEvent.clientX - containerRect.left) / containerRect.width) * 100;
        if (newWidth > 20 && newWidth < 80) {
          leftPanelWidth.value = newWidth;
        }
      };
      
      const onMouseUp = () => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      };
      
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    };
    
    const onDefinitionSaved = (saved) => {
      if (!saved) return;
      existingRequirements.value = saved.requirements || existingRequirements.value;
      checkDefinitionName.value = saved.name || checkDefinitionName.value;
      columnCellPolicy.value = saved.column_cell_policy || columnCellPolicy.value;
    };

    onMounted(async () => {
      document.addEventListener('click', onDocumentClick);
      if (route.query.tab === 'check') {
        activeTab.value = 'check';
      }
      await Promise.all([loadDocuments(), loadExistingRequirements()]);
      const qId = route.query.requirementId || route.query.requirement_id;
      if (qId) {
        const idNum = Number(qId);
        selectedExistingId.value = Number.isNaN(idNum) ? qId : idNum;
        loadExistingRequirement();
      }
    });

    onBeforeUnmount(() => {
      document.removeEventListener('click', onDocumentClick);
    });

    watch(() => route.query.tab, (tab) => {
      if (tab === 'check') activeTab.value = 'check';
    });

    watch(requirementFingerprint, () => {
      if (savedSnapshot.value && requirementFingerprint.value !== savedSnapshot.value) {
        // editing after a test clears "tested but not saved" only when they change again after test — keep flag until save
      } else if (!isDirty.value) {
        draftTestedUnsaved.value = false;
      }
    });

    watch(selectedDocumentId, (newId) => {
      if (newId) {
        const doc = documents.value.find(d => d.id === newId);
        if (doc && !doc.file_url) {
          fetchDocumentUrl(newId);
        }
      }
    });
    
    return {
      projectId,
      canAccessResearchCampaigns,
      leftPanelWidth,
      activeTab,
      checkDefinitionId,
      checkDefinitionName,
      onDefinitionSaved,
      detailsOpen,
      saving,
      snapshotting,
      documents,
      selectedDocumentId,
      existingRequirements,
      selectedExistingId,
      isEditingExisting,
      selectedDocument,
      filteredRequirements,
      groupedFilteredRequirements,
      groupedExistingRequirements,
      policyChipLabel,
      filteredDocuments,
      rulePickerOpen,
      docPickerOpen,
      ruleSearch,
      docSearch,
      rulePickerEl,
      docPickerEl,
      pdfUrl,
      isNonPreviewable,
      requirement,
      saveStatus,
      isDirty,
      draftStatusLabel,
      draftStatusClass,
      livePromptPreview,
      spatialGrounding,
      spatialGroundingLabel,
      DOCUMENT_TYPES,
      COVERAGE_TYPES,
      PROMPT_TYPES,
      messages,
      inputMessage,
      isTyping,
      chatContainer,
      testing,
      testResult,
      lastResponseTime,
      lastPromptStats,
      lastTestModel,
      responseTimeClass,
      loadingVersions,
      versionHistory,
      compareVersionId,
      selectedCompareVersion,
      // Test Suite state
      testSuiteSelections,
      testSuiteResults,
      testSuiteRunning,
      testSuiteRunningAll,
      expandedTestResults,
      testSuiteTiming,
      testSuiteTotalTime,
      estimatedProductionTime,
      selectedRulesCount,
      allSelected,
      testSuiteHasResults,
      testSuiteSummary,
      testSuiteDigestGroups,
      truncateText,
      expandFailuresOnly,
      expandAllWithResults,
      collapseAllExpanded,
      scrollToAndExpandRule,
      // Image upload state
      chatImages,
      isDraggingImage,
      imageUploadError,
      imageInput,
      // Methods
      sendMessage,
      sendSuggestion,
      useRequirement,
      loadExistingRequirement,
      runTest,
      architectActionLabel,
      feedbackToArchitect,
      executeAction,
      saveToDefinition,
      snapshotCurrentPrompt,
      deleteRequirement,
      toggleSelectAll,
      isPolicyGroupSelected,
      isPolicyGroupPartial,
      togglePolicyGroupSelection,
      runTestSuite,
      runSingleTest,
      getReqSummaryLine,
      getReqDescription,
      getReqPromptTemplate,
      getReqPromptType,
      getReqPromptTypeLabel,
      getReqFieldDescription,
      getReqExpectedValue,
      formatExpectedValue,
      editRuleFromTestSuite,
      toggleTestResultExpand,
      getTimingClass,
      loadVersionHistory,
      revertToVersion,
      formatDate,
      formatShortDate,
      documentTypeLabel,
      documentCoverageLabel,
      toggleRulePicker,
      toggleDocPicker,
      selectDocument,
      startNewRule,
      openSavedRule,
      formatMessage,
      getResultIcon,
      startResize,
      // Image upload methods
      triggerImageInput,
      handleImageSelect,
      handleImageDrop,
      removeChatImage,
      formatFileSize
    };
  }
};
</script>

<style scoped>
.requirement-lab {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 3.5rem);
  max-height: calc(100vh - 3.5rem);
  background: #f1f5f9;
  overflow: hidden;
}

.lab-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 1rem 2rem;
  background: white;
  border-bottom: 1px solid #e2e8f0;
  z-index: 10;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.btn-back {
  color: #64748b;
  font-size: 1.25rem;
}

.header-title h1 {
  font-size: 1.25rem;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.header-title p {
  margin: 0;
  font-size: 0.875rem;
  color: #64748b;
}

.header-right {
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: flex-end;
}

.rule-picker,
.doc-picker {
  position: relative;
  min-width: 16rem;
  max-width: 22rem;
}

.picker-field {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.picker-label {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #64748b;
}

.picker-trigger {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.15rem;
  width: 100%;
  text-align: left;
  padding: 0.55rem 1.75rem 0.55rem 0.75rem;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  background: #fff;
  cursor: pointer;
  position: relative;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.picker-trigger:hover,
.picker-trigger[aria-expanded="true"] {
  border-color: var(--brand-accent, #2563eb);
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
}

.picker-trigger-main {
  font-size: 0.875rem;
  font-weight: 600;
  color: #0f172a;
  line-height: 1.25;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.picker-trigger-meta {
  font-size: 0.75rem;
  color: #64748b;
  line-height: 1.3;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.picker-caret {
  position: absolute;
  right: 0.65rem;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 0.75rem;
}

.picker-code {
  display: inline-block;
  margin-left: 0.35rem;
  padding: 0.05rem 0.35rem;
  border-radius: 4px;
  background: #f1f5f9;
  color: #475569;
  font-size: 0.7rem;
  font-weight: 600;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}

.picker-menu {
  position: absolute;
  top: calc(100% + 0.35rem);
  right: 0;
  left: 0;
  z-index: 40;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.14);
  overflow: hidden;
  min-width: 20rem;
}

.doc-picker-menu {
  min-width: 24rem;
  max-width: min(32rem, 90vw);
  left: auto;
}

.picker-menu-head {
  display: flex;
  gap: 0.5rem;
  padding: 0.65rem;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
}

.picker-search {
  flex: 1;
  min-width: 0;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 0.45rem 0.65rem;
  font-size: 0.85rem;
}

.picker-menu-list {
  max-height: 22rem;
  overflow-y: auto;
  padding: 0.35rem;
}

.picker-group-label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.45rem 0.55rem 0.25rem;
  margin-top: 0.25rem;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #64748b;
}

.picker-group-label:first-child {
  margin-top: 0;
}

.picker-group-count {
  font-weight: 600;
  letter-spacing: 0;
  text-transform: none;
  color: #94a3b8;
}

.picker-policy-chip,
.ts-policy-chip {
  display: inline-block;
  padding: 0.05rem 0.35rem;
  border-radius: 999px;
  background: #e0e7ff;
  color: #3730a3;
  font-size: 0.68rem;
  font-weight: 700;
}

.picker-option {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  width: 100%;
  text-align: left;
  border: none;
  background: transparent;
  border-radius: 8px;
  padding: 0.55rem 0.65rem;
  cursor: pointer;
}

.picker-option:hover,
.picker-option.active {
  background: #eff6ff;
}

.picker-option-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: #0f172a;
  line-height: 1.25;
}

.picker-option-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem 0.55rem;
  font-size: 0.72rem;
  color: #64748b;
}

.picker-option-sub {
  font-size: 0.7rem;
  color: #94a3b8;
}

.doc-option-meta .meta-entity {
  color: #1e3a8a;
  font-weight: 600;
}

.doc-option-meta .meta-muted {
  color: #94a3b8;
  font-style: italic;
}

.doc-option-meta .meta-type,
.doc-option-meta .meta-coverage {
  padding: 0.05rem 0.35rem;
  border-radius: 999px;
  background: #f1f5f9;
  color: #334155;
}

.picker-empty {
  margin: 0.75rem;
  text-align: center;
  color: #94a3b8;
  font-size: 0.85rem;
}

.doc-badge.entity-badge,
.doc-type-chip.entity-chip {
  background: #e0e7ff;
  color: #312e81;
}

.split-pane-container.settings-mode {
  gap: 0;
}

.check-settings-pane {
  height: 100%;
  overflow: hidden;
}

.split-pane-container {
  display: flex;
  flex: 1;
  overflow: hidden;
  position: relative;
}

.document-panel, .lab-panel {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
}

.lab-panel {
  background: white;
}

.panel-header {
  padding: 0.75rem 1.5rem;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.panel-header h3 {
  font-size: 0.9375rem;
  margin: 0;
  color: #334155;
}

.document-content {
  flex: 1;
  background: #cbd5e1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.document-content iframe {
  flex: 1;
  min-height: 400px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  flex: 1;
  text-align: center;
  padding: 2rem;
}

.empty-state.non-previewable {
  background: #f8fafc;
}

.empty-state .muted {
  color: #64748b;
  font-size: 0.9rem;
  margin-top: 0.5rem;
  color: #64748b;
}

.resizer {
  width: 8px;
  cursor: col-resize;
  background: #e2e8f0;
  transition: background 0.2s;
}

.resizer:hover {
  background: #3b82f6;
}

.tabs {
  display: flex;
  gap: 1.5rem;
}

.tabs button {
  padding: 0.5rem 0;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  font-size: 0.9375rem;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
}

.tabs button:hover:not(:disabled) {
  color: var(--p-accent, #1e3a5f);
}

.tabs button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.tabs button.active {
  color: var(--p-accent, #1e3a5f);
  border-bottom-color: var(--p-accent, #1e3a5f);
}

.panel-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: white;
  overflow: hidden;
  min-height: 0;
}

.tab-pane {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

/* Architect Pane */
.architect-pane {
  padding: 0;
}

.chat-messages {
  flex: 1;
  padding: 1.5rem;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.message {
  display: flex;
  max-width: 85%;
}

.message.user {
  align-self: flex-end;
}

.message.assistant {
  align-self: flex-start;
}

.message-content {
  padding: 1rem;
  border-radius: 12px;
  font-size: 0.9375rem;
  line-height: 1.5;
}

.user .message-content {
  background: #3b82f6;
  color: white;
}

.assistant .message-content {
  background: #f1f5f9;
  color: #1e293b;
}

.chat-input {
  padding: 1rem 1.5rem;
  border-top: 1px solid #e2e8f0;
  display: flex;
  gap: 0.75rem;
  background: white;
  align-items: flex-end;
  transition: all 0.2s ease;
}

.chat-input.dragging {
  background: #eff6ff;
  border-color: #3b82f6;
}

.chat-input textarea {
  flex: 1;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0.75rem;
  resize: none;
}

.chat-input .attach-btn {
  width: 40px;
  height: 40px;
  background: #f1f5f9;
  color: #64748b;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.chat-input .attach-btn:hover:not(:disabled) {
  background: #e2e8f0;
  color: #3b82f6;
}

.chat-input .attach-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.chat-input .send-btn {
  width: 48px;
  height: 40px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
}

.chat-input .send-btn:disabled {
  background: #94a3b8;
  cursor: not-allowed;
}

/* Chat Image Previews */
.chat-image-previews {
  display: flex;
  gap: 0.5rem;
  padding: 0.5rem 1.5rem;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
  flex-wrap: wrap;
}

.chat-image-preview {
  position: relative;
  width: 60px;
  height: 60px;
  border-radius: 8px;
  overflow: hidden;
  border: 2px solid #e2e8f0;
}

.chat-image-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.chat-image-preview .pdf-icon {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fef2f2;
  color: #dc2626;
  font-size: 1.5rem;
}

.chat-image-preview .remove-image {
  position: absolute;
  top: 2px;
  right: 2px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.6rem;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.chat-image-preview:hover .remove-image {
  opacity: 1;
}

.image-error {
  padding: 0.5rem 1.5rem;
  background: #fef2f2;
  color: #dc2626;
  font-size: 0.8rem;
}

/* Editor Pane */
.editor-pane {
  padding: 0;
  gap: 0;
  overflow: hidden;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.editor-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 1rem 1.25rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.identity-row .grow {
  flex: 2;
}

.checkbox-accuracy-panel {
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 0.65rem;
  padding: 0.85rem 1rem;
}

.checkbox-accuracy-panel .panel-help {
  margin: 0 0 0.65rem;
  font-size: 0.8rem;
  color: #92400e;
  line-height: 1.35;
}

.checkbox-chip {
  background: #fef3c7 !important;
  color: #92400e !important;
}

.prompt-hero .prompt-editor {
  min-height: 14rem;
}

.editor-details {
  border: 1px solid #e2e8f0;
  border-radius: 0.55rem;
  background: #f8fafc;
}

.editor-details summary {
  list-style: none;
  cursor: pointer;
  padding: 0.65rem 0.85rem;
  font-weight: 650;
  font-size: 0.88rem;
  color: #334155;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.editor-details-body {
  padding: 0 0.85rem 0.85rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.editor-sticky-footer {
  flex-shrink: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1.25rem;
  border-top: 1px solid #e2e8f0;
  background: #fff;
  box-shadow: 0 -4px 12px rgba(15, 23, 42, 0.04);
}

.editor-sticky-actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.draft-badge {
  font-size: 0.78rem;
  font-weight: 700;
  padding: 0.25rem 0.55rem;
  border-radius: 999px;
  background: #f1f5f9;
  color: #475569;
}

.draft-badge.draft-dirty {
  background: #fff7ed;
  color: #c2410c;
}

.draft-badge.draft-tested {
  background: #eff6ff;
  color: #1d4ed8;
}

.draft-badge.draft-saved {
  background: #ecfdf5;
  color: #047857;
}

.diagnostic-only-note {
  margin: 0.5rem 0 0;
  font-size: 0.8rem;
  color: #b45309;
  font-weight: 600;
}

.spatial-strip {
  display: flex;
  align-items: flex-start;
  gap: 0.45rem;
  margin-top: 0.65rem;
  padding: 0.55rem 0.7rem;
  border-radius: 0.45rem;
  font-size: 0.8rem;
  line-height: 1.35;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  color: #334155;
}

.spatial-strip.spatial-engaged {
  background: #ecfdf5;
  border-color: #a7f3d0;
  color: #065f46;
}

.spatial-strip.spatial-unavailable {
  background: #fff7ed;
  border-color: #fed7aa;
  color: #9a3412;
}

.spatial-strip.spatial-skipped,
.spatial-strip.spatial-empty {
  background: #f8fafc;
  color: #475569;
}

.form-row {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.half {
  flex: 1;
  min-width: 8rem;
}

.prompt-editor {
  font-family: 'Fira Code', monospace;
  font-size: 0.875rem;
  background: #fafafa;
}

.editor-actions {
  padding: 0.5rem 0;
  flex-shrink: 0;
}

.lab-parity-note {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.625rem 0.875rem;
  margin-bottom: 0;
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 8px;
  color: #0c4a6e;
  font-size: 0.8rem;
  line-height: 1.45;
}
.lab-parity-note i {
  margin-top: 0.15rem;
  color: #0284c7;
  flex-shrink: 0;
}

.model-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.125rem 0.5rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 500;
  color: #3730a3;
  background: #eef2ff;
}

.test-results-container {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
  margin-top: 1rem;
  margin-bottom: 1.5rem;
  flex-shrink: 0;
}

.result-body {
  padding: 1rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.result-section {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.result-section label {
  font-weight: 600;
  font-size: 0.875rem;
  color: #475569;
}

.result-section p {
  margin: 0;
  font-size: 0.9375rem;
  line-height: 1.6;
  color: #334155;
}

.result-section ul {
  margin: 0;
  padding-left: 1.25rem;
}

.result-section li {
  margin-bottom: 0.25rem;
  font-size: 0.9375rem;
}

.result-footer {
  padding: 1rem 1.5rem;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  gap: 1rem;
}

.result-footer p {
  margin: 0;
  font-size: 0.875rem;
  color: #64748b;
}

.result-header {
  padding: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 700;
}

.result-header.pass { background: #dcfce7; color: #166534; }
.result-header.fail { background: #fee2e2; color: #991b1b; }
.result-header.partial { background: #fef3c7; color: #92400e; }

.evidence-box {
  background: #f8fafc;
  padding: 1rem;
  border-radius: 8px;
  border-left: 4px solid #cbd5e1;
  font-size: 0.875rem;
  white-space: pre-wrap;
}

.proposed-req {
  margin-top: 1rem;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 1.25rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.req-prompt {
  background: #f8fafc;
  padding: 0.75rem;
  border-radius: 6px;
  font-size: 0.8125rem;
  margin: 0.75rem 0;
  border: 1px solid #e2e8f0;
}

.req-prompt pre {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
}

.badge.critical { background: #ef4444; color: white; }
.badge.high { background: #f97316; color: white; }
.badge.medium { background: #eab308; color: white; }
.badge.low { background: #64748b; color: white; }

.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1.25rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all 0.2s;
}

.btn-primary { background: var(--p-accent, #1e3a5f); color: white; }
.btn-outline { background: white; border: 1px solid #e2e8f0; color: #475569; }
.btn-success { background: #10b981; color: white; }
.btn-sm { padding: 0.4rem 0.75rem; font-size: 0.8125rem; }
.btn-lg { padding: 1rem 2rem; }

.welcome-message {
  text-align: center;
  padding: 2rem 1.5rem;
  max-width: 28rem;
  margin: 0 auto;
}

.welcome-message .ai-avatar {
  width: 3rem;
  height: 3rem;
  margin: 0 auto 0.75rem;
  border-radius: 999px;
  background: #f1f5f9;
  color: var(--p-accent, #1e3a5f);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.35rem;
}

.welcome-message h3 {
  margin: 0 0 0.4rem;
  color: #0f172a;
}

.welcome-message p {
  margin: 0 0 1rem;
  color: #475569;
  font-size: 0.9rem;
  line-height: 1.4;
}

.suggestions {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.suggestion-chip {
  display: block;
  width: 100%;
  text-align: left;
  padding: 0.65rem 0.85rem;
  border-radius: 0.55rem;
  border: 1px solid #cbd5e1;
  background: #fff;
  color: #0f172a;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.suggestion-chip:hover,
.suggestion-chip:focus-visible {
  border-color: var(--p-accent, #1e3a5f);
  background: #f8fafc;
  outline: none;
}

.form-control {
  width: 100%;
  padding: 0.625rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

pre { margin: 0; }
code { font-family: monospace; }

/* Version History Styles */
.versions-pane {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
}

.versions-header {
  margin-bottom: 1.5rem;
}

.versions-header h4 {
  margin: 0 0 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.versions-header .subtitle {
  color: #64748b;
  margin: 0;
}

.loading-state, .empty-versions {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1.5rem 1rem;
  color: #64748b;
  text-align: center;
}

.empty-versions i {
  color: #94a3b8;
  margin: 0.75rem 0 0.5rem;
}

.empty-versions .muted {
  max-width: 28rem;
  margin: 0.35rem auto 1rem;
  font-size: 0.85rem;
  line-height: 1.4;
}

.empty-versions .version-card {
  width: 100%;
  text-align: left;
  margin-bottom: 0.75rem;
}

.version-card {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 1rem;
  margin-bottom: 1rem;
}

.version-card.active-version {
  border-color: #10b981;
  background: #f0fdf4;
}

.version-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.version-badge {
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
}

.version-badge.active {
  background: #10b981;
  color: white;
}

.version-badge.feedback {
  background: #8b5cf6;
  color: white;
}

.version-label {
  font-weight: 600;
  color: #1e293b;
}

.prompt-preview {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 1rem;
  max-height: 200px;
  overflow-y: auto;
}

.prompt-preview pre {
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 0.875rem;
  line-height: 1.5;
  margin: 0;
}

.comparison-view {
  border: 2px solid #3b82f6;
  border-radius: 12px;
  padding: 1rem;
  margin-bottom: 1.5rem;
  background: #f8fafc;
}

.comparison-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.comparison-header h5 {
  margin: 0;
}

.comparison-panels {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1rem;
}

.compare-panel {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 1rem;
}

.compare-panel label {
  display: block;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #64748b;
}

.compare-panel pre {
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 0.8125rem;
  max-height: 300px;
  overflow-y: auto;
}

.comparison-actions {
  display: flex;
  justify-content: flex-end;
}

.version-list h5 {
  margin: 1.5rem 0 1rem;
  color: #64748b;
}

.version-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  margin-bottom: 0.5rem;
  background: white;
}

.version-item.is-active {
  border-color: #10b981;
  background: #f0fdf4;
}

.version-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.version-number {
  font-weight: 700;
  color: #1e293b;
}

.version-date {
  color: #64748b;
  font-size: 0.875rem;
}

.version-meta {
  flex: 1;
  color: #64748b;
  font-size: 0.875rem;
}

.version-notes {
  font-style: italic;
}

.version-actions {
  display: flex;
  gap: 0.5rem;
}

.btn-ghost {
  background: transparent;
  color: #64748b;
  border: none;
}

.btn-ghost:hover {
  background: #f1f5f9;
  color: #1e293b;
}

.btn-warning {
  background: #f59e0b;
  color: white;
}

.btn-warning:hover {
  background: #d97706;
}

.btn-danger {
  background: #ef4444;
  color: white;
}

.btn-danger:hover {
  background: #dc2626;
}

/* Rule Context Banner */
.rule-context-banner {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 0.25rem;
  flex-shrink: 0;
}

.banner-blue {
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  color: #1d4ed8;
}

.banner-green {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  color: #15803d;
}

.code-chip {
  background: rgba(0, 0, 0, 0.08);
  padding: 0.125rem 0.5rem;
  border-radius: 4px;
  font-family: monospace;
  font-size: 0.8125rem;
}

.banner-delete-btn {
  margin-left: auto;
}

/* Prompt Character Counter */
.prompt-char-counter {
  display: block;
  margin-top: 0.375rem;
  font-size: 0.8125rem;
  color: #94a3b8;
  text-align: right;
}

.counter-warn {
  color: #d97706;
  font-weight: 600;
}

/* Editor actions layout */
.editor-actions {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

/* Test Suite Tab */
.testsuite-pane {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.testsuite-header {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.testsuite-header-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
}

.testsuite-title h4 {
  margin: 0 0 0.25rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.testsuite-title .subtitle {
  margin: 0;
  font-size: 0.875rem;
  color: #64748b;
}

.testsuite-warning {
  background: #fffbeb;
  border: 1px solid #fde68a;
  color: #92400e;
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  font-size: 0.875rem;
}

.testsuite-controls {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.selection-counter {
  font-size: 0.875rem;
  color: #64748b;
  font-weight: 500;
}

.testsuite-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  flex: 1;
}

.testsuite-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.testsuite-group-head {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0 0.15rem;
}

.testsuite-group-head h5 {
  margin: 0;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: #334155;
}

.testsuite-group-count {
  font-size: 0.72rem;
  font-weight: 600;
  color: #94a3b8;
}

.ts-group-select {
  display: flex;
  align-items: center;
}

.testsuite-row {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: white;
  overflow: hidden;
  transition: border-color 0.2s;
}

.testsuite-row.row-pass { border-color: #86efac; }
.testsuite-row.row-fail { border-color: #fca5a5; }
.testsuite-row.row-partial { border-color: #fcd34d; }
.testsuite-row.row-error { border-color: #cbd5e1; }
.testsuite-row.row-expanded { border-color: #93c5fd; box-shadow: 0 1px 0 rgba(59, 130, 246, 0.08); }

.testsuite-row-main {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
}

.ts-expand-toggle {
  flex-shrink: 0;
  margin-top: 0.15rem;
  width: 1.75rem;
  height: 1.75rem;
  border: none;
  border-radius: 6px;
  background: #f8fafc;
  color: #64748b;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.ts-expand-toggle:hover {
  background: #eff6ff;
  color: #2563eb;
}

.ts-checkbox {
  flex-shrink: 0;
  cursor: pointer;
  margin-top: 0.2rem;
}

.ts-checkbox input[type="checkbox"] {
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.ts-rule-info {
  flex: 1;
  min-width: 0;
  text-align: left;
}

.ts-rule-info-clickable {
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
}

.ts-rule-info-clickable:hover .ts-rule-name {
  color: #2563eb;
}

.ts-rule-headline {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.ts-rule-preview {
  margin: 0.35rem 0 0;
  font-size: 0.8125rem;
  line-height: 1.45;
  color: #64748b;
}

.ts-result-snippet {
  display: block;
  font-weight: 500;
}

.ts-result-snippet.fail { color: #b91c1c; }
.ts-result-snippet.partial { color: #b45309; }
.ts-result-snippet.pass { color: #15803d; }
.ts-result-snippet.error { color: #475569; }

.testsuite-results-digest {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #fff;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.digest-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  flex-wrap: wrap;
}

.digest-header h5 {
  margin: 0 0 0.25rem;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.digest-subtitle {
  margin: 0;
  font-size: 0.8125rem;
  color: #64748b;
}

.digest-actions {
  display: flex;
  gap: 0.35rem;
  flex-wrap: wrap;
}

.digest-group h6 {
  margin: 0 0 0.5rem;
  font-size: 0.8125rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.digest-group.digest-fail h6 { color: #b91c1c; }
.digest-group.digest-partial h6 { color: #b45309; }
.digest-group.digest-pass h6 { color: #15803d; }
.digest-group.digest-error h6 { color: #475569; }

.digest-card {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0.65rem 0.75rem;
  background: #f8fafc;
  margin-bottom: 0.5rem;
}

.digest-card-head {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-bottom: 0.35rem;
}

.digest-excerpt {
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.45;
  color: #334155;
}

.digest-evidence {
  margin: 0.35rem 0 0;
  font-size: 0.8125rem;
  color: #64748b;
  font-style: italic;
}

.digest-pass-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.digest-pass-chip {
  border: 1px solid #bbf7d0;
  background: #f0fdf4;
  color: #166534;
  border-radius: 999px;
  padding: 0.25rem 0.65rem;
  font-size: 0.8125rem;
  cursor: pointer;
}

.digest-pass-chip:hover {
  background: #dcfce7;
}

.digest-timing {
  opacity: 0.75;
  margin-left: 0.25rem;
}

.summary-badge.error {
  background: #f1f5f9;
  color: #475569;
}

.ts-rule-name {
  font-weight: 600;
  color: #1e293b;
  font-size: 0.9375rem;
}

.ts-code-chip {
  background: #f1f5f9;
  color: #475569;
  padding: 0.125rem 0.4rem;
  border-radius: 4px;
  font-family: monospace;
  font-size: 0.75rem;
  flex-shrink: 0;
}

.ts-severity-badge {
  padding: 0.125rem 0.4rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  flex-shrink: 0;
}

.ts-severity-badge.critical { background: #fee2e2; color: #991b1b; }
.ts-severity-badge.high { background: #ffedd5; color: #c2410c; }
.ts-severity-badge.medium { background: #fef9c3; color: #854d0e; }
.ts-severity-badge.low { background: #f1f5f9; color: #475569; }

.ts-result-area {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
  margin-top: 0.1rem;
}

.ts-run-btn {
  flex-shrink: 0;
  margin-top: 0.05rem;
}

.ts-spinner {
  color: #3b82f6;
  font-size: 1rem;
}

.ts-result-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8125rem;
  font-weight: 700;
}

.ts-result-badge.pass { background: #dcfce7; color: #166534; }
.ts-result-badge.fail { background: #fee2e2; color: #991b1b; }
.ts-result-badge.partial { background: #fef3c7; color: #92400e; }
.ts-result-badge.error { background: #f1f5f9; color: #475569; }

.ts-confidence {
  font-weight: 400;
  opacity: 0.8;
}

.ts-expand-btn {
  padding: 0.25rem 0.5rem;
}

.ts-edit-btn {
  flex-shrink: 0;
  margin-top: 0.05rem;
}

.ts-details {
  padding: 0 1rem 1rem 3.25rem;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.ts-check-details,
.ts-result-details {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.ts-check-details h6,
.ts-result-details h6 {
  margin: 0;
  font-size: 0.8125rem;
  font-weight: 700;
  color: #334155;
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.ts-check-lead {
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.5;
  color: #334155;
}

.ts-check-lead.ts-muted {
  color: #64748b;
  font-style: italic;
}

.ts-meta-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 0.65rem 1rem;
  margin: 0;
}

.ts-meta-grid div {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.ts-meta-grid dt {
  margin: 0;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
}

.ts-meta-grid dd {
  margin: 0;
  font-size: 0.875rem;
  color: #1e293b;
}

.ts-prompt-preview {
  margin: 0;
  padding: 0.75rem;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.8125rem;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 240px;
  overflow: auto;
  color: #334155;
}

.ts-result-details {
  padding-top: 0.75rem;
  border-top: 1px dashed #cbd5e1;
}

.ts-no-result-hint {
  font-size: 0.875rem;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.ts-detail-section {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.ts-detail-section label {
  font-weight: 600;
  font-size: 0.8125rem;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.ts-detail-section p {
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.5;
  color: #334155;
}

.ts-detail-section ul {
  margin: 0;
  padding-left: 1.25rem;
}

.ts-detail-section li {
  font-size: 0.9rem;
  margin-bottom: 0.25rem;
}

/* Test Suite Summary */
.testsuite-summary {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  flex-shrink: 0;
}

.summary-label {
  font-weight: 700;
  color: #475569;
  font-size: 0.875rem;
}

.summary-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8125rem;
  font-weight: 700;
}

.summary-badge.pass { background: #dcfce7; color: #166534; }
.summary-badge.fail { background: #fee2e2; color: #991b1b; }
.summary-badge.partial { background: #fef3c7; color: #92400e; }
.summary-badge.timing { background: #f0f9ff; color: #0369a1; }
.summary-badge.timing.slow { background: #fef2f2; color: #b91c1c; }
.summary-badge.production-estimate.ok { background: #ecfdf5; color: #065f46; }
.summary-badge.production-estimate.warn { background: #fef2f2; color: #b91c1c; font-weight: 600; }

/* ── Response Time Indicators ──────────────────────────────── */
.response-time {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.125rem 0.5rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 500;
}
.response-time.fast { color: #065f46; background: #ecfdf5; }
.response-time.moderate { color: #92400e; background: #fffbeb; }
.response-time.slow { color: #b91c1c; background: #fef2f2; }

.result-meta {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.ts-timing {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.75rem;
  padding: 0.125rem 0.5rem;
  border-radius: 10px;
  font-weight: 500;
}
.ts-timing.fast { color: #065f46; background: #ecfdf5; }
.ts-timing.moderate { color: #92400e; background: #fffbeb; }
.ts-timing.slow { color: #b91c1c; background: #fef2f2; }

/* ── Prompt Type & Filter Fields ───────────────────────────── */
.label-hint {
  font-size: 0.78rem;
  color: #94a3b8;
  font-weight: 400;
  margin-left: 0.4rem;
}

.section-divider {
  font-size: 0.8rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid #e2e8f0;
  padding-bottom: 0.4rem;
  margin-bottom: 0.75rem;
}

.checkbox-extra-fields {
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 8px;
  padding: 0.875rem 1rem;
  margin-bottom: 1rem;
}

.filter-checkbox-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.4rem;
}

.filter-chip {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.3rem 0.65rem;
  border: 1px solid #cbd5e1;
  border-radius: 20px;
  font-size: 0.82rem;
  cursor: pointer;
  transition: all 0.15s;
  user-select: none;
  white-space: nowrap;
}

.filter-chip:hover {
  border-color: #6366f1;
  background: #eef2ff;
}

.filter-chip input[type="checkbox"] {
  margin: 0;
  width: 13px;
  height: 13px;
  accent-color: #6366f1;
}

/* Save status banner */
.save-status-banner {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 0.875rem;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  margin-bottom: 0.75rem;
  animation: fadeIn 0.2s ease;
}

.save-status-success { background: #dcfce7; color: #166534; border: 1px solid #bbf7d0; }
.save-status-error { background: #fee2e2; color: #991b1b; border: 1px solid #fecaca; }

@keyframes fadeIn { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }

/* Doc context banner in test suite */
.doc-context-banner {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.4rem;
  padding: 0.5rem 0.75rem;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.85rem;
  color: #475569;
}

.doc-type-chip {
  background: #dbeafe;
  color: #1e40af;
  padding: 0.15rem 0.5rem;
  border-radius: 20px;
  font-size: 0.78rem;
  font-weight: 600;
}

.filter-note { color: #94a3b8; font-size: 0.78rem; }

/* Test suite row type chips */
.ts-type-chip {
  background: #f0f9ff;
  color: #0284c7;
  border: 1px solid #bae6fd;
  padding: 0.1rem 0.45rem;
  border-radius: 12px;
  font-size: 0.73rem;
  font-weight: 600;
  text-transform: capitalize;
}

.ts-filter-chip {
  background: #fafafa;
  color: #64748b;
  border: 1px solid #e2e8f0;
  padding: 0.1rem 0.45rem;
  border-radius: 12px;
  font-size: 0.73rem;
  cursor: default;
}
</style>

